<template>
  <div>
    <exo-form
      hideClose
      :root="_self"
      :clear="false"
      :beta="inputs.type === 'LEO Station Keeping'"
    >
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          margin="m-0"
          v-if="inputs.type === 'Orbital Transfer'"
          title="Orbital Transfer"
        >
          <b>General description</b>
          <br />
          This corresponds to a non-coplanar orbit transfer where both
          semi-major axis and inclination are simultaneously changed. The
          propulsion system is fired around the orbit’s nodes in order to
          improve the overall manoeuvre efficiency. This manoeuvre can be
          simulated both using a numerical or a semi-analytical propagator.
          Different perturbations can be considered while performing this
          manoeuvre: Earth potential, third-body (Sun, Moon), atmospheric drag
          and solar radiation pressure.
          <br />
          <br />
          In general, the firing arcs are simply computed from the duty cycle,
          which in turn is defined as the ratio between the on-board average
          power (OAP) and the propulsion system power. For example, a duty cycle
          of 0.5 will correspond to two 90 degrees arcs around the ascending and
          descending node.
          <br />
          <br />
          If the spacecraft’s system is simulated, thrusting will only be
          possible if the solar array or the battery can deliver enough power to
          fire the propulsion system. Propulsion system warm-up is modelled by
          considering an increased (fixed) power consumption for a given
          duration before the beginning of each firing arc.
        </exo-help-dialog>

        <exo-help-dialog
          :color="'white--text'"
          margin="m-0"
          v-if="inputs.type === 'LEO Station Keeping'"
          title="LEO Station Keeping"
        >
          <b>General description</b>
          <br />
          For a spacecraft in LEO orbit the semi-major axis decays under the
          influence of atmospheric drag. This mission performs periodic
          station-keeping maneuvers to restore the semi-major axis to a value
          above the initial mean semi-major axis. This value is specified by the
          user through the "sma top tolerance". Manoeuvres are triggered when
          the semi-major axis reach a certain value, specified in the mission
          configuration through the "sma bottom tolerance".
          <br />
          Instead of defining a box in semi-major axis, the user can specify an
          along-track box in which the spacecraft must remain.
          <br />
          The perturbation of the Earth potential can be considered as well,
          although it does not affect the secular dynamics of the semi-major
          axis or the inclination. The inclination is restored as well, by
          performing a combined manoeuvre, when the difference between the
          current and reference mean value is greater than
          <katex-element expression="\Delta i = 0.01" /> deg.
          <br />
          <br />
          Given the impact that the spacecraft's orientation has on the
          resulting drag force, the attitude can be simulated in this mission.
          The user can decide which mode should be selected during
          non-manoeuvring phases by choosing between a Sun-pointing and a
          nadir-pointing mode. In the last case a custom axis can be selected as
          nadir-pointing direction
        </exo-help-dialog>

        <exo-help-dialog
          :color="'white--text'"
          margin="m-0"
          v-if="inputs.type === 'RAAN Phasing'"
          title="RAAN Phasing Mission"
        >
          <b>General description</b>
          <br />
          The RAAN phasing mission allows to simultaneously perform a specified
          change in mean semi-major axis, inclination as well as relative RAAN
          with respect to a given reference. This can be either the trajectory
          of a spacecraft performing an orbital transfer from the initial to the
          final orbits or a specified target orbit (see the
          <i> RAAN phasing reference</i> tooltip below).
          <br />
          <br />
          This mission provides a means of distributing spacecrafts over several
          orbital planes during the deployment of a constellation. Since it can
          be very time and fuel consuming an optimisation is performed (see
          below). This mission is simulated with perturbations: an Earth
          potential model (<katex-element expression="8\times 8" /> model for
          LEO) is always active, while the atmospheric drag can be optionally
          considered.
          <br />
          <br />
          <b>Manoeuvre strategy</b>
          <br />
          The idea of the mission is to exploit the RAAN drift rate caused by
          the
          <katex-element expression="J_2" />
          term of the Earth potential.
          <br />
          The spacecraft manoeuvres from an initial orbit to a coasting orbit.
          After a while, a second manoeuvre is performed from the coasting orbit
          to a final orbit, which may or may not be different from the initial
          orbit. The target value for the relative RAAN change
          <katex-element expression="\Delta\Omega" />
          with respect to the chosen reference trajectory is built-up by the
          difference in RAAN drift rate during the mission.
          <br />
          <br />
          <b>Coasting orbit computation</b>
          <br />
          The coasting semi-major axis, inclination as well as the coasting
          duration are computed by iterative optimisation. This is done using an
          analytical model for the manoeuvre which guarantees fast computation.
          The performance index to be optimised can be one of:
          <br />
          <br />
          <ul>
            <li>
              <katex-element expression="\Delta V" />: the fuel consumption to
              achieve a given <katex-element expression="\Delta\Omega" /> is
              minimised for a user-specified mission duration.
            </li>
            <li>
              <katex-element expression="\Delta T" />: manoeuvre time to achieve
              a given <katex-element expression="\Delta\Omega" /> is minimised
              for a user-specified maximum
              <katex-element expression="\Delta V" /> value.
            </li>
            <li>
              <katex-element expression="\Delta \Omega" />: the RAAN variation
              is maximised for a user-specified maximum
              <katex-element expression="\Delta V" /> value.
            </li>
          </ul>
          <b>Taking into account the atmospheric drag</b>
          <br />
          When the atmospheric drag is simulated, the spacecraft performs
          regular station keeping manoeuvres in order to keep its semi-major
          axis in a <katex-element expression="\pm" />1 km interval around the
          nominal value for the coasting orbit. The number of performed station
          keeping manoeuvres will be added to the results.
          <br />
          <br />

          <b>Simulating the attitude and the power system</b>
          <br />
          As it is the case for other simulations missions, the power system of
          the spacecraft can be simulated during RAAN phasing. A few remarks:
          <br />
          <br />
          <ul>
            <li>
              <b>Simulation duration</b>: simulating the power system will
              inevitably slow down the simulation since a number of additional
              states are simulated and the corresponding outputs are generated.
              The attention of the user is attracted on the following fact: a
              badly dimensioned power system could slow down the simulation to
              an unacceptable level (if for example the solar arrays and the
              battery can not produce enough power to operate the propulsion
              system). If in doubt, the user is advised to use the
              <i>Maneuvering Strategy Design mission</i>.
            </li>

            <li>
              <b>Coasting phase</b>: the attitude of the spacecraft is not
              maintained during the coasting phase. System ephemerides still
              exist for this phase.
            </li>
          </ul>
          <br />
          <br />
          <b>Example 1: constellation deployement scenario.</b>
          <br />
          Let's consider the following mission scenario: two spacecraft have
          been injcted on the same 600 km circular Sun synchronous orbit (SSO).
          Both need to reach a 800 km circular SSO orbit. Moreover, the
          spacecrafts needs to be deployed on two different orbital planes
          having 25 degrees RAAN difference at the end of the mission. The RAAN
          phasing simulation mission allows to quickly compute the manoeuvre
          characteristics (consumption, duration) as well as plotting the
          evolution of the orbital elements. For this case, the user will select
          <i>direct transfer</i> in the RAAN phasing reference menu of the
          configuration page.
          <br />
          <br />
          <v-img
            contain
            :src="
              require('../../assets/images/description-raan-phasing-example-1.png')
            "
            aspect-ratio="2"
          />
          <br />
          <br />
          <b>Example 2: replacing a spacecraft in a constellation.</b>
          <br />
          Consider a spare satellite that needs to replace a broken member of a
          constellation. We suppose the spare's orbit is identified by its
          altitude (666 km), inclination (86.2 deg) and RAAN (60 deg at
          simulation date). A manoeuvre will be performed by specifying the
          target orbit of the satellite to be replaced. The target altitude is
          (780 km), inclination (86.4) and RAAN (0 deg) at the same date the at
          which the initial orbit has been specified. At the end of the
          simulation, the value of the semi-major axis and inclination of the
          replacement spacecraft will match those specified for the target
          orbit. The relative RAAN with respect to the target plane will be 0:
          the spare spacecraft has successfully integrated the constellation.
          For this case, the user will select <i>final orbit</i> in the RAAN
          phasing reference menu of the configuration page.
          <br />
          <br />
          <v-img
            contain
            :src="
              require('../../assets/images/description-raan-phasing-example-2.png')
            "
            aspect-ratio="2"
          />
          <br />
          <br />
          <br />
        </exo-help-dialog>
        <exo-help-dialog
          :color="'white--text'"
          margin="m-0"
          v-if="inputs.type === 'Orbit Phasing'"
          title="Orbit Phasing"
        >
          <b>General description</b>
          <br />
          This mission allows to modify the mean anomaly of the spacecraft on
          its orbit by the specified amount ΔAOL. This means that at the end of
          the manoeuvre the angular separation with respect to an unperturbed
          satellite following the initial orbit will equal ΔAOL. The variation
          in the spacecraft's angular velocity as a function of the semi-major
          axis is exploited to achieve the desired change. No perturbation are
          taken into account for the computation of this manoeuvre.
          <br />
          A typical scenario is the post-injection deployment of a 2-spacecraft
          constellation on a single plane. The spacecrafts are injected at the
          same point and need to reach the operational configuration, where they
          are separated by ΔAOL = 180 deg.
          <br />
          <br />
          This manoeuvre consists of the following distinct phases:
          <br />
          - A first tangential thrust brings the spacecraft from the initial
          user-specified orbit to the drift altitude.
          <br />
          - A coasting phase (no firing of the propulsion systems) at drift
          altitude
          <br />
          - A final manoeuvre to bring the satellite back to the initial
          altitude, at the desired location.
          <br />
          <br />
          The drift altitude is computed using an iterative numerical
          root-finding method.
          <br />
          <br />
          <b>Example</b>
          Let's consider a spacecraft into a 600km SSO which needs to perform a
          rendez-vous with another spacecraft located on the same orbit but with
          an angular separation of 10 degrees. The following figure (generated
          using the "Orbit Phasing" simulation mission) shows the manoeuvre
          needed in order to perform the aforementioned manoeuvre.
          <br />
          <br />
          <v-img
            contain
            :src="require('../../assets/images/description-orbit-phasing.png')"
            aspect-ratio="1.8"
          />
          <br />
          <br />
        </exo-help-dialog>
        <exo-help-dialog
          :color="'white--text'"
          margin="m-0"
          v-if="isOAPMission"
          title="Maneuvering Strategy Design"
        >
          <b>General description</b>
          <br />
          When the power system of the spacecraft is simulated, the available
          power is influenced by the orientation (attitude) of the spacecraft
          which dictates the amount of lighting received by the solar arrays.
          <br />
          <br />
          This mission estimates the mean available power for a given system
          configuration and a given orbit. A number of different attitude modes
          are simulated in order to reproduce different mission phases. One
          attitude mode is set for thrust arcs and one for rest arcs and orbits.
          <br />
          <br />
          <b>Note:</b> no maneuvers are performed during this mission. However a
          prograde mode is simulated in order to mimic an altitude change.
          <br />
          <br />
          <b>Duty cycle:</b>
          the results provided by this mission can be used to define the duty
          cycle value for a given system.
        </exo-help-dialog>
        <exo-help-dialog
          :color="'white--text'"
          margin="m-0"
          v-if="inputs.type === 'Orbit Extrapolation'"
          title="Orbit Extrapolation"
        >
          <b>General description</b>
          <br />
          Extrapolate an orbit using a numerical or semi-analytical propagator
          under the effects of perturbations.
          <br />
          The semi analytical propagator can be used to efficiently study the
          evolution of the mean orbital elements over very long duration
          (several years).
          <br />
          <br />
          <b>Attitude</b>
          <br />
          The orientation of the spacecraft has a strong influence on the drag
          force experienced by a spacecraft in LEO. This mission is performed
          with the following orientation of spacecraft frame:
          <br />
          <li>
            <katex-element expression="\mathbf Y_{\mathrm{sat}}" />: normal to
            the orbital plane aligned with the opposite of the angular momentum
            vector;
          </li>
          <li>
            <katex-element expression="\mathbf Z_{\mathrm{sat}}" />: pointing
            towards the center of the Earth;
          </li>
          <li>
            <katex-element expression="\mathbf X_{\mathrm{sat}}" />: given by
            the vector product
            <katex-element
              expression="\mathbf Y_{\mathrm{sat}} \times \mathbf Z_{\mathrm{sat}}"
            />. This is in the direction of motion and aligned with the
            (inertial) velocity vector for a circular orbit.
          </li>
          <br />
          A few remarks:
          <br />
          1) If the user wishes to simulate a different attitude then the
          spacecraft axes needs to be redefined accordingly (in the Shape menu).
          <br />
          2) If the main concern is the simulation of the effects of the
          atmospheric drag, then a spherical geometry can be used.
          <br />
          3) It is worth recalling that the "Maneuvering Strategy Design"
          mission allows to estimate an equivalent drag area in a number of
          different attitude modes (check the detailed results of this mission).
          <br />
          <br />
          <b>Note:</b> even though the propulsion system is not used for this
          mission it needs to be defined.
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" />

      <v-row>
        <v-col cols="6">
          <exo-card-form label="Initial conditions">
            <exo-input-ref-notparametric
              :root="_self"
              name="spacecraft"
              type="spacecraft"
              componentRoute="/numericalspacecraft"
            />
            <div
              v-if="inputs.type === 'Orbit Extrapolation'"
              class="v-messages"
            >
              Even if a propulsion system is required for each spacecraft, it
              won't be used in this mission, and the results won't depend on the
              propulsion system.
            </div>
            <exo-input-ref-notparametric
              :root="_self"
              name="initialOrbit"
              type="orbit"
              componentRoute="/numericalorbit"
            />
            <exo-form-alert type="error" v-if="displayErrorEccentricityRAAN">
              Sorry, RAAN phasing missions can only be performed on orbits with
              an eccentricity lower than 0.01
            </exo-form-alert>

            <v-row v-if="this.inputs.initialOrbit != null">
              <v-col> Orbit Date : {{ getDateTimeString() }} </v-col>
            </v-row>
            <exo-input-checkbox
              v-if="this.inputs.initialOrbit != null"
              :root="_self"
              name="overloadOrbitDateTime"
            >
              <exo-help-dialog :title="getLabel('overloadOrbitDateTime')">
                <b>Warning</b>
                <br />
                The simulation date is set to the orbit date by default. When
                changing the mission start date, the orbit date is implicitly
                adjusted to the new one. No propagation of the orbital state to
                the new date is performed. In other words, things go as if the
                orbit had been originally defined at the new start date and the
                mission start date had not been changed.<br />
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-input-date-time
              v-if="this.inputs.overloadOrbitDateTime"
              :root="_self"
              name="missionDateTime"
            />
          </exo-card-form>
          <exo-card-form
            label="Mission"
            v-if="inputs.type !== 'Maneuvering Strategy Design'"
          >
            <exo-input-select :root="_self" name="targetDateDefinitionType" />
            <exo-input-double :root="_self" name="duration" />
            <exo-input-date-time :root="_self" name="targetDate" />
            <exo-form-alert v-if="targetDateBeforeStartDate"
              >The target date is before the start date</exo-form-alert
            >
            <exo-input-select
              :root="_self"
              name="toleranceType"
              tooltip="Deviation allowed with respect to the initial orbit"
              :disabledItemConfiguration="disableToleranceType"
            />
            <exo-form-alert
              v-if="spacecraftShapeMessage !== ''"
              :type="spacecraftShapeMessageType"
            >
              {{ spacecraftShapeMessage }}
            </exo-form-alert>
            <exo-input-double
              :root="_self"
              name="alongTrackTolerance"
              tooltip="Along track deviation allowed with respect to the initial orbit"
            >
              <exo-help-dialog
                title="Along-track tolerance"
                v-if="this.inputs.toleranceType === 'ALONG_TRACK_TOLERANCE'"
              >
                <tooltipAlongTrackTolerance />
              </exo-help-dialog>
            </exo-input-double>
            <exo-form-alert
              type="warning"
              v-if="isRequiredOrOptional('alongTrackTolerance', inputs)"
            >
              The along-track tolerance is only used to define an effective
              semi-major axis margin. Consequently, the error in the velocity
              direction may be higher than expected. Please read the tooltip for
              more information.
            </exo-form-alert>
            <v-row>
              <v-col cols="5" class="mr-10">
                <exo-input-double
                  :root="_self"
                  name="smaTopMargin"
                  tooltip="Semi-major axis top deviation allowed with respect to the initial orbit"
                />
              </v-col>

              <v-col cols="5" class="mr-2">
                <exo-input-double
                  :root="_self"
                  name="smaBottomMargin"
                  tooltip="Semi-major axis bottom deviation allowed with respect to the initial orbit"
                />
              </v-col>
              <exo-help-dialog
                title="Sma tolerance"
                v-if="this.inputs.toleranceType === 'SMA_TOLERANCE'"
              >
                <tooltipSmaTopBottomMargin />
              </exo-help-dialog>
            </v-row>

            <exo-input-select :root="_self" name="targetDefinition">
              <exo-help-dialog title="Destination definition">
                <p>
                  The destination of mission can be specified by individual
                  change of orbital elements or by specifying a target orbit.
                </p>
                <p v-if="inputs.type === 'RAAN Phasing'">
                  If a target orbit is specified, the change of orbital elements
                  will be automatically evaluated (only for semi-major axis and
                  inclination). Additionally, if the RAAN phasing reference is
                  set to 'Final orbit', the ascending node change
                  (<katex-element expression="\Delta" />RAAN/<katex-element
                    expression="\Delta"
                  />MLTAN) can also be automatically computed to reach the
                  target orbit ascending node position (RAAN/MLTAN).
                </p>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-ref-notparametric
              :root="_self"
              name="targetOrbit"
              type="orbit"
              componentRoute="/numericalorbit"
            />
            <exo-input-double
              :root="_self"
              :disabled="deltaOrbitalElementsReadOnly"
              name="deltaSemiMajorAxis"
            >
            </exo-input-double>
            <exo-input-double
              :root="_self"
              :disabled="deltaOrbitalElementsReadOnly"
              name="deltaEccentricity"
            >
              <exo-help-dialog title="Eccentricity change">
                The eccentricity target must remain in
                <katex-element expression="[0,1[" />, only elliptical orbit are
                supported.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-double
              :root="_self"
              :disabled="deltaOrbitalElementsReadOnly"
              name="deltaInclination"
            >
            </exo-input-double>
            <exo-input-select :root="_self" name="orbitPhasingOptimizationType">
              <exo-help-dialog title="Optimisation type">
                <table>
                  <tbody>
                    <tr>
                      <td>ΔV &nbsp;</td>
                      <td>
                        Minimise the ΔV and thus the propellant needed to
                        fulfill the mission.
                      </td>
                    </tr>
                    <tr>
                      <td>ΔAOL &nbsp;</td>
                      <td>Maximise the relative* phasing angle.</td>
                    </tr>
                    <tr>
                      <td>ΔT &nbsp;</td>
                      <td>Minimise the duration of the mission.</td>
                    </tr>
                  </tbody>
                </table>
                <br />
                *ΔAOL must be understood as a relative mean argument of latitude
                phasing with respect to the initial orbit.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double
              :root="_self"
              name="targetDeltaArgumentOfLatitude"
            />
            <exo-input-double
              :root="_self"
              name="targetMissionDurationForAol"
            />
            <exo-input-select :root="_self" name="raanPhasingReference">
              <exo-help-dialog title="Reference trajectory for RAAN phasing">
                Two different choices can be made for the reference trajectory
                with respect to which the RAAN phasing is performed:
                <br />
                <br />
                <li>
                  <b>Direct transfer:</b> the RAAN phasing is performed with
                  respect to a spacecraft performing an orbital transfer between
                  the initial and the final orbits. This case is of interest for
                  two satellites injected at the same point that need to be
                  deployed on two orbits having identical semi-major axis and
                  inclination and whose ascending nodes are separated by the
                  prescribed <katex-element expression="\Delta \Omega" />. See
                  figure below.
                </li>
                <v-img
                  contain
                  :src="
                    require('../../assets/images/description-raan-phasing.png')
                  "
                  aspect-ratio="2"
                />
                <li>
                  <b>Final orbit:</b> A target orbit, specified by its
                  semi-major axis <katex-element expression="a_f" />,
                  inclination <katex-element expression="i_f" /> and RAAN
                  <katex-element expression="\Omega_f" />. This orbit is
                  specified <b>on the same date as the initial orbit</b> (which
                  is also equal to the simulation date). This case can be used
                  to simulate a spacecraft that is launched via a rideshare
                  solution and that needs to reach a target plane, where other
                  spacecrafts might have already been deployed.
                </li>
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-raan-phasing-rdv.png')
                  "
                  aspect-ratio="2"
                />
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-select :root="_self" name="optimizationType">
              <exo-help-dialog title="Optimisation type">
                <table>
                  <tbody>
                    <tr>
                      <td>ΔV &nbsp;</td>
                      <td>
                        Minimise the ΔV and thus the propellant needed to
                        fulfill the mission.
                      </td>
                    </tr>
                    <tr>
                      <td>ΔRAAN+ / ΔRAAN- &nbsp;</td>
                      <td>
                        Maximise the relative* RAAN phasing (positive or
                        negative change of RAAN).
                      </td>
                    </tr>
                    <tr>
                      <td>ΔT &nbsp;</td>
                      <td>Minimise the duration of the mission.</td>
                    </tr>
                  </tbody>
                </table>
                <br />
                *Here ΔRAAN must be understood as a relative RAAN phasing with
                respect to the chosen RAAN phasing reference. See mission
                description for more details.
              </exo-help-dialog>
            </exo-input-select>

            <exo-input-double :root="_self" name="targetMissionDuration">
              <exo-help-dialog title="Target mission duration">
                This is the duration expected for the whole mission, including
                initial manoeuvre, drift and final manoeuvre.<br />
                Warning : up to 10% of difference can be observe between the
                effective duration and the expected one.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-double :root="_self" name="targetDeltaVmax" />
            <exo-input-select :root="_self" name="targetRaanLtanType" />

            <v-row no-gutters>
              <v-col v-if="showUseTargetOrbitRaanOption">
                <v-checkbox
                  v-model="useFinalOrbitRaan"
                  label="Use target orbit ascending node position"
                ></v-checkbox>
              </v-col>
              <v-col>
                <exo-input-double
                  :root="_self"
                  :disabled="useFinalOrbitRaan"
                  name="targetDeltaLtan"
                >
                  <exo-help-dialog title="ΔMLTAN">
                    ΔMLTAN must be understood as the MLTAN difference with
                    respect to the chosen RAAN phasing reference. See mission
                    description for more details.
                  </exo-help-dialog>
                </exo-input-double>
                <exo-form-alert
                  type="error"
                  v-if="this.displayErrorTargetDeltaLtan"
                >
                  Error: ΔMLTAN must be different from 0h
                </exo-form-alert>
                <exo-input-double
                  :root="_self"
                  :disabled="useFinalOrbitRaan"
                  name="targetDeltaRaan"
                >
                  <exo-help-dialog title="ΔRAAN">
                    Here ΔRAAN must be understood as a relative RAAN phasing
                    with respect to the chosen RAAN phasing reference. See
                    mission description for more details.
                  </exo-help-dialog>
                </exo-input-double>
              </v-col>
            </v-row>

            <exo-input-select :root="_self" name="coastingDomain">
              <exo-help-dialog title="Coasting domain">
                The RAAN phasing mission finds the optimal coasting orbit with
                respect to the chosen optimisation scenario. It is possible to
                limit the admissible altitude and inclination of the coasting
                orbit.
                <br />
                <br />
                By default, the coasting altitude can vary between 250 km and
                50000 km.
                <br />
                By default, the coasting inclination can vary between 0 and 180
                degrees.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double :root="_self" name="minCoastingAltitude" />
            <exo-form-alert type="error" v-if="displayErrorCoastingAltitude">
              Max coasting altitude must be higher than min coasting altitude.
            </exo-form-alert>
            <exo-input-double :root="_self" name="maxCoastingAltitude" />
            <exo-input-double :root="_self" name="minCoastingInclination" />
            <exo-form-alert type="error" v-if="displayErrorCoastingInclination">
              Max coasting inclination must be higher than min coasting
              inclination.
            </exo-form-alert>
            <exo-input-double :root="_self" name="maxCoastingInclination" />
          </exo-card-form>
          <exo-card-form
            v-if="isRequiredOrOptional('maneuveringStrategyChoice', inputs)"
            label="Manoeuvring strategies"
          >
            <exo-input-select
              :root="_self"
              name="maneuveringStrategyChoice"
              :disabledItemConfiguration="disabledManoeuvringStrategy"
            >
              <exo-help-dialog title="Manoeuvring strategies">
                The default manoeuvring strategy is to thrust on two symmetric
                arcs around the ascending and descending nodes.
                <br />
                <br />
                A custom mode allows the user to modify the manoeuvring
                strategy. Depending on the orbital elements changed in the
                mission (semi-major axis, eccentricity, inclination, RAAN or
                argument of latitude), the user can change the number of thrust
                arcs (1 or 2), their position, and the thrust pattern.
                <br />
                <br />
                This custom mode is taken into account only in case of low
                thrust maneuver, for impulsive maneuver the maneuver strategy is
                automatically determine to achieve the requested maneuver
                depending on perigee argument of orbit :
                <ul>
                  <li>
                    If perigee argument is low (below 45°) all elements (semi
                    major axis, inclination and eccentricity) can be controlled
                    at the same time. The best reference (apogee/perigee or
                    ascending/descending nodes) is chosen depending on the
                    requested maneuver.
                  </li>
                  <li>
                    If perigee argument is high (above or equal to 45°) all
                    elements (semi major axis, inclination and eccentricity)
                    cannot be controlled at the same time. The orbital transfer
                    is then done sequentially with semi major axis and
                    eccentricity maneuver for one hand et inclination maneuver
                    for the other hand.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-form-alert
              type="error"
              v-if="
                inputs.maneuveringStrategyChoice === 'CUSTOM' &&
                inputs.propagationType === 'SEMI_ANALYTICAL'
              "
            >
              Sorry, custom strategies are not yet available with a
              semi-analytical propagation
            </exo-form-alert>
            <exo-input-double
              :root="_self"
              name="thrustArcMeanLongitudeArgument"
            >
            </exo-input-double>
            <v-row
              no-gutters
              v-if="isRequiredOrOptional('thrustArcsNumber', inputs)"
            >
              <v-col cols="5">
                <exo-input-select
                  :disabled="unitaryDutyCycle"
                  :root="_self"
                  name="thrustArcsNumber"
                  :items="thrustArcsNumberItems"
                >
                </exo-input-select>
              </v-col>
              <v-col cols="6">
                <exo-input-select
                  :disabled="unitaryDutyCycle"
                  :root="_self"
                  name="thrustArcsPosition"
                  :items="thrustArcsPositionItems"
                >
                </exo-input-select>
              </v-col>
              <v-col cols="1" xl class="m-3 p-2">
                <exo-help-dialog
                  title="Thrust arcs position"
                  margin="mt-3 ml-2"
                >
                  The thrust arcs position can be chosen from a number of
                  predefined positions which depend on the number of arcs and on
                  the orbital elements modified during the mission.
                  <br />
                  <br />
                  When the eccentricity is not controlled, the "Custom" option
                  makes it possible to place the burn arcs anywhere on the
                  orbit.
                </exo-help-dialog>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="isRequiredOrOptional('reference', inputs)">
              <v-col cols="5">
                <exo-input-select
                  :root="_self"
                  name="reference"
                  :items="thrustArcsPositionItemsNotCustom"
                >
                </exo-input-select>
              </v-col>
              <v-col cols="6" class="pl-1">
                <exo-input-double
                  :root="_self"
                  name="deltaMeanLongitudeArgument"
                >
                </exo-input-double>
              </v-col>
              <v-col cols="1" class="m-3 p-2">
                <exo-help-dialog
                  title="Custom arcs position"
                  margin="mt-3 ml-2"
                >
                  Two parameters are needed to define custom arcs:
                  <br />
                  1/ a reference point among the proposed alternatives;
                  <br />
                  2/ an angle between the reference point and the centre of the
                  burn arc.
                  <br />
                  <br />
                  <v-img
                    contain
                    :src="
                      require('../../assets/images/tooltip-custom-thrust-arcs.png')
                    "
                    aspect-ratio="2.5"
                  />
                </exo-help-dialog>
              </v-col>
            </v-row>
            <v-row
              no-gutters
              v-if="isRequiredOrOptional('numberOfThrustOrbits', inputs)"
            >
              <v-col cols="3">
                <exo-input-double :root="_self" name="numberOfThrustOrbits">
                </exo-input-double>
              </v-col>
              <v-col cols="4" class="pl-1">
                <exo-input-double :root="_self" name="numberOfRestOrbits">
                </exo-input-double>
              </v-col>
              <v-col cols="4" class="pl-1">
                <exo-input-double
                  :disabled="unitaryDutyCycle"
                  :root="_self"
                  name="numberOfShiftOrbits"
                >
                </exo-input-double>
              </v-col>
              <v-col cols="1" class="m-3 p-2"> </v-col>
            </v-row>
            <v-row
              align="center"
              no-gutters
              v-if="isRequiredOrOptional('numberOfThrustOrbits', inputs)"
            >
              <v-col cols="11">
                <thrustPattern
                  :tp_thrust="inputs.numberOfThrustOrbits"
                  :tp_rest="inputs.numberOfRestOrbits"
                  :tp_shift="inputs.numberOfShiftOrbits"
                  :tp_arc="inputs.thrustArcsNumber"
                  :tp_duty_cycle="inputs.orbitalDutyCycle"
                >
                </thrustPattern>
              </v-col>
              <v-col cols="1" class="m-0 p-0">
                <exo-help-dialog title="Thrust patterns" margin="mt-1 ml-2">
                  <ul>
                    <li>
                      "Thrust orbits" represents the number of consecutive
                      orbits on which burns will be performed.
                    </li>
                    <li>
                      "Rest orbits" represents the number of consecutive orbits
                      on which no burn will be performed.
                    </li>
                    <li>
                      In two-burn strategies involving symmetric burns arcs,
                      these can be shifted from one another by a whole number of
                      revolutions. This is described by the "shift orbits"
                      parameter.
                    </li>
                  </ul>
                  Thrust patterns are represented below on one pattern period.
                  The period of a pattern is defined as the sum of the number of
                  thrust orbits and of the number of rest orbits.
                </exo-help-dialog>
              </v-col>
            </v-row>
            <exo-form-alert
              type="error"
              v-if="
                isRequiredOrOptional('numberOfThrustOrbits', inputs) &&
                unconsistantThrustPattern
              "
            >
              The shift parameter should be strictly lower than the sum of
              thrust and rest orbits.
            </exo-form-alert>
            <exo-input-checkbox
              :root="_self"
              name="stopThrustInEclipse"
              :onChange="updateEclipseFlag"
            >
              <exo-help-dialog
                title="Stop thrusting during eclipse"
                margin="mt-1 ml-2"
              >
                This option allows to authorize or prevent thrusting while being
                in eclipse.
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-input-checkbox
              :root="_self"
              name="dynamicDutyCycleSimulated"
              :onChange="updateEclipseFlag"
            >
              <exo-help-dialog
                title="Simulate dynamic duty cycle"
                margin="mt-1 ml-2"
              >
                <h3>Definition</h3>
                <p>
                  This option allows to update the duty cycle during the
                  simulation. For each manoeuvre cycle, the following steps are
                  carried out.
                </p>
                <ol>
                  <li>
                    Compute eclipse ratio
                    <katex-element
                      expression="\varepsilon_n = \Delta t_\mathrm{eclipse} / \Delta t_\mathrm{cycle}"
                    />.
                  </li>
                  <li>
                    Compute eclipse coefficient
                    <katex-element
                      expression="c_n = \varepsilon_{n-1} / \varepsilon_n"
                    />.
                  </li>
                  <li>
                    Compute new arc length
                    <katex-element expression="l_n = c_n \cdot l_{n-1}" />.
                  </li>
                  <li>Update the duty cycle.</li>
                </ol>
                <p>
                  Here,
                  <katex-element expression="\Delta t_\mathrm{eclipse}" /> and
                  <katex-element expression="\Delta t_\mathrm{cycle}" />
                  are respectively the eclipse and the manoeuvre cycle
                  durations.
                </p>
                <h3>Ephemerides</h3>
                <p>
                  To output the dynamic duty cycle ephemerides at the end of the
                  simulation, select "Manoeuvring Strategies" in the Ephemerides
                  Generation menu.
                </p>
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-form-alert
              type="warning"
              v-if="
                inputs.stopThrustInEclipse && inputs.dynamicDutyCycleSimulated
              "
            >
              Current method of dynamic duty cycle needs to thrust during
              eclipse. Errors may occur.
            </exo-form-alert>
            <v-row
              no-gutters
              v-if="isRequiredOrOptional('thrustArcDefinition', inputs)"
            >
              <v-col cols="5">
                <exo-input-select :root="_self" name="thrustArcDefinition">
                </exo-input-select>
              </v-col>
              <v-col cols="6">
                <exo-input-double
                  :disabled="inputs.thrustArcDefinition !== 'THRUST_DURATION'"
                  :onChangeCallback="thrustArcDurationUpdate"
                  :root="_self"
                  name="thrustArcDuration"
                >
                </exo-input-double>
              </v-col>
              <v-col cols="1">
                <exo-help-dialog
                  title="Thrust arc definition"
                  margin="mt-3 ml-2"
                >
                  The length of thrust arcs can be defined from the orbital duty
                  cycle or from a fixed thrust duration. When the thrust
                  duration is chosen, it is automaticaly converted into the
                  equivalent orbital duty cycle at the initial orbit. If the
                  orbital period changes during the maneuver, e.g. long
                  maneuver, the thrust duration might not be respected.
                </exo-help-dialog>
              </v-col>
            </v-row>
            <exo-input-slider
              :disabled="inputs.thrustArcDefinition === 'THRUST_DURATION'"
              :onChangeCallback="dutyCycleUpdate"
              :root="_self"
              v-if="isRequiredOrOptional('orbitalDutyCycle', inputs)"
              :max="maxOrbitalDutyCycle"
              name="orbitalDutyCycle"
            >
              <exo-help-dialog title="Orbital duty cycle">
                The orbital duty cycle
                <katex-element expression="\xi" /> represents the percentage of
                an orbit for which the propulsion system will be triggered.
                <br />
                <br />
                If <katex-element expression="\xi" /> is set to 100%, one burn
                will last for a whole orbit. If set to a lower value, one or two
                burns will be performed on finite arcs of the orbit.
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-orbital-duty-cycle.png')
                  "
                  aspect-ratio="1.8"
                />
                <br />
                By default, <katex-element expression="\xi" /> is equal to the
                ratio between the orbital average power available for propulsion
                and the nominal power of the propulsion system. A bigger
                <katex-element expression="\xi" /> will decrease the number of
                burns needed to perform a mission, but it will increase the
                thrusting duration and the propellant mass needed for manoeuvres
                involving inclination changes. On the contrary, a smaller
                <katex-element expression="\xi" /> will increase the number of
                burns required to perform a mission, but it will decrease the
                thrusting duration and the propellant mass needed for manoeuvres
                involving inclination changes. This is because inclination
                changes are more efficient when the burns are performed closer
                to the orbital nodes.
                <br />
                <v-img
                  contain
                  :src="require('../../assets/images/duty-cycle-tradeoff.png')"
                  aspect-ratio="1.8"
                />
                <br />
                <br />
                If <katex-element expression="\xi" /> is chosen greater than the
                ratio between the orbital average power available for propulsion
                and the nominal power of the electrical propulsion system, the
                time interval between thrusting manoeuvres needs to be increased
                to preserve the maximum value of the average duty cycle
                achievable. This can be done by modifying the thrust pattern
                used.
              </exo-help-dialog>
            </exo-input-slider>
            <exo-form-alert type="error" v-if="errorThrustDuration !== ''">
              {{ errorThrustDuration }}
            </exo-form-alert>
            <exo-form-alert
              type="grey"
              v-if="isRequiredOrOptional('orbitalDutyCycle', inputs)"
            >
              The maximum orbital duty cycle without rest orbit is
              {{ Math.round(maxMeanDutyCycle * 10) / 10 }} %
            </exo-form-alert>
            <exo-form-alert
              type="warning"
              v-if="
                isRequiredOrOptional('orbitalDutyCycle', inputs) &&
                inputs.orbitalDutyCycle > 60
              "
            >
              A high orbital duty cycle might be inefficient when changing the
              inclination of an orbit. Please have a look at the help item.
            </exo-form-alert>
          </exo-card-form>
          <exo-card-form
            v-if="isRequiredOrOptional('thrustAttitudeMode', inputs)"
            label="Thrust and rest attitude modes"
          >
            <exo-input-select :root="_self" name="thrustAttitudeMode">
              <exo-help-dialog title="Thrust attitude modes">
                <tooltipThrustAttitudeMode />
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-checkbox
              :disabled="1 === 2"
              :root="_self"
              name="customManeuver"
            >
              <exo-help-dialog title="Hypothetical maneuver">
                <tooltipHypotheticalManeuver />
              </exo-help-dialog>
            </exo-input-checkbox>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-5 ml-2">
                <exo-input-double
                  :root="_self"
                  name="thrustCustomPointingAxisT"
                />
              </v-col>
              <v-col cols="3" class="mr-5">
                <exo-input-double
                  :root="_self"
                  name="thrustCustomPointingAxisN"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="thrustCustomPointingAxisW"
                />
              </v-col>
              <v-col cols="3" class="mr-5 ml-2">
                <exo-input-double
                  :root="_self"
                  name="maneuverDeltaSma"
                  tooltip="Semi-major axis change of the hypothetical maneuver (km)"
                />
              </v-col>
              <v-col cols="3" class="mr-5">
                <exo-input-double
                  :root="_self"
                  name="maneuverDeltaInc"
                  tooltip="Inclination change of the hypothetical maneuver (deg)"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="maneuverDeltaEcc"
                  tooltip="Eccentricity change of the hypothetical maneuver"
                />
              </v-col>
            </v-row>
            <v-row no-gutters justify="start">
              <exo-form-alert
                class="mb-2"
                type="error"
                v-if="
                  this.inputs.thrustAttitudeMode === 'THRUST_CUSTOM' &&
                  isNotValidThrustCustomAxis
                "
              >
                Error: The norm of the direction can not be 0.
              </exo-form-alert>
            </v-row>
            <exo-input-select
              :root="_self"
              name="restAttitudeMode"
              :items="restAttitudeModeItems"
            >
              <exo-help-dialog title="Rest attitude modes">
                The thrust attitude mode corresponds to the satellite's attitude
                outside the propulsion arcs. It can either be:
                <ul>
                  <li>
                    <b>Sun pointing</b>: The orientation of the satellite is
                    calculated to maximize the lighting received by the solar
                    arrays.
                  </li>
                  <br />
                  <li>
                    <b>Yaw steering</b>: The +Z axis in the satellite's frame
                    points towards the Earth's center. During this mode, a yaw
                    manoeuvre is performed to optimise lighting of the solar
                    arrays.
                  </li>
                  <br />
                  <li>
                    <b>Nadir pointing</b>: A user-specified axis in satellite's
                    frame points towards the Earth's center.
                  </li>
                  <br />
                  <li>
                    <b>Prograde</b>: The propulsion system axis is aligned with
                    the inertial velocity vector. A rotation around this axis is
                    performed to optimise the lighting of the solar array.
                  </li>
                  <br />
                  <li>
                    <b>Normal</b>: The propulsion system axis is aligned with
                    the direction of the orbit's angular momentum vector (that
                    is, perpendicular to the orbit plane). A rotation around the
                    propulsion system axis is performed to optimise the lighting
                    of the solar array.
                  </li>
                  <br />
                  <li>
                    <b>Radial</b>: The propulsion system axis is aligned with
                    the vector from the planet's center to the satellite. A
                    rotation around the propulsion system axis is performed to
                    optimise the lighting of the solar array.
                  </li>
                  <br />
                  <li>
                    <b>Custom</b>: The propulsion system axis is aligned with a
                    direction vector, in the TNW local orbital frame. A rotation
                    around the propulsion system axis is performed to optimise
                    the lighting on the solar array.
                  </li>
                  <br />
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <v-row
              no-gutters
              v-if="inputs.restAttitudeMode === 'REST_CUSTOM'"
              justify="start"
            >
              <v-col cols="3" class="mr-5 ml-2">
                <exo-input-double
                  :root="_self"
                  name="restCustomPointingAxisT"
                />
              </v-col>
              <v-col cols="3" class="mr-5">
                <exo-input-double
                  :root="_self"
                  name="restCustomPointingAxisN"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="restCustomPointingAxisW"
                />
              </v-col>
            </v-row>
            <v-row no-gutters justify="start">
              <exo-form-alert
                class="mb-2"
                type="error"
                v-if="
                  this.inputs.restAttitudeMode === 'REST_CUSTOM' &&
                  isNotValidRestCustomAxis
                "
              >
                Error: The norm of the direction can not be 0.
              </exo-form-alert>
            </v-row>
          </exo-card-form>
        </v-col>
        <v-col cols="6">
          <exo-card-form label="Simulation configuration">
            <exo-input-double :root="_self" name="maximumDuration">
              <exo-help-dialog title="Maximum simulation duration">
                This is the maximum duration expected for the mission. If the
                target is not reached after that duration, the mission would
                fail. This parameter is used to avoid unpredicted long
                simulation.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-select :root="_self" name="propagationType">
              <exo-help-dialog title="Propagation type">
                Two type of propagation can be performed:
                <br />
                <br />
                <b>Numerical propagation</b>: this gives the most accurate
                results at the expenses of an increased computational load. The
                integrator used is an adaptative step Dormand-Prince which
                guarantees high precision. The maximum step is set to 100
                seconds and the minimum one to 0.01 seconds.
                <br />
                <br />
                <b>Semi-analytical propagation</b>: this allows to propagate the
                mean orbital elements (as per the DSST theory) using a very
                large step. This results in very fast yet accurate propagations.
                An adaptative step integrator is used (Dormand-Prince). The step
                is computed as a function of the orbital period and its maximum
                value is set to 20 times the Keplerian orbital period. Eclipse,
                attitude and power system can not be simulated in
                semi-analytical propagation.
              </exo-help-dialog>
            </exo-input-select>
            <exo-form-alert
              type="info"
              v-if="this.inputs.type === 'Orbit Phasing'"
            >
              The earth potential perturbation is not currently supported by
              orbit phasing simulation.
            </exo-form-alert>
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="perturbations"
              :disabledItemConfiguration="disabledPerturbationsConfiguration"
            >
              <exo-help-dialog title="Perturbations">
                <tooltipPerturbations />
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double
              :root="_self"
              v-if="
                inputs.perturbations != null &&
                inputs.perturbations.includes('DRAG')
              "
              name="customSolarFlux"
            >
              <exo-help-dialog title="Solar flux">
                The value of the solar flux
                <katex-element expression="F_{10.7}" /> is correlated to the
                atmospheric density. As a rule of thumb, increasing the solar
                flux increases the density. For instance:
                <br />
                <ul>
                  <li>
                    solar flux 65 corresponds to low solar activity and minimum
                    density.
                  </li>
                  <li>solar flux 150 is an average value.</li>
                  <li>
                    solar flux 275 corresponds to strong solar activity and
                    maximum density.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-double>
            <v-row
              no-gutters
              class="ml-0"
              v-if="
                isRequiredOrOptional('earthPotentialConfiguration', inputs) &&
                inputs.perturbations != null &&
                inputs.perturbations.includes('EARTH_POTENTIAL')
              "
            >
              <v-col cols="4">
                <exo-input-select
                  :root="_self"
                  name="earthPotentialConfiguration"
                >
                  <exo-help-dialog title="Earth potential">
                    The EGM96 model is used. The default values for the degree
                    <katex-element expression="n" /> and order
                    <katex-element expression="m" /> of the model depend on the
                    orbit's semi-major axis <katex-element expression="a" /> as
                    reported below:
                    <br />
                    <br />
                    <katex-element
                      expression="(n, m) = (8, 8) \text{  for  } a \leq 13000 \text{ km}"
                    />;<br />
                    <katex-element
                      expression="(n, m) = (6, 6) \text{  for  } 13000 \text{ km} < a < 26000 \text{ km}"
                    />;<br />
                    <katex-element
                      expression="(n, m) = (4, 4) \text{  for  }a \geq 26000 \text{ km}"
                    />.<br />
                    <br />
                    <br />
                    These values can be changed by the user by using a custom
                    configuration.<br />
                  </exo-help-dialog>
                </exo-input-select>
              </v-col>
              <v-col cols="3" class="mr-4 ml-6">
                <exo-input-double
                  :root="_self"
                  name="customEarthPotentialDegree"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="customEarthPotentialOrder"
                />
              </v-col>
            </v-row>
            <exo-form-alert type="info" v-if="isOAPMission">
              In this mission the earth potential pertubation is used to take
              the J2 effect into account.
            </exo-form-alert>
            <exo-input-checkbox
              :disabled="isOAPMission || !isNumericalPropagation"
              :root="_self"
              :onChange="updateEclipseFlag"
              name="attitudeAndSystemSimulated"
            >
              <exo-help-dialog title="Attitude Modes">
                Different attitude modes can be simulated when doing simulation
                missions. The following first two modes are available for all
                missions:
                <br />
                <br />
                <b>Manoeuvre</b>
                The propulsion system axis is aligned with the instantaneous
                thrust direction. We suppose that no other constraints exist and
                that a rotation around the propulsion system axis is possible to
                optimise the solar array orientation.
                <br />
                Note: if the solar array is non-steerable the power received
                during thrust might be sub-optimal, depending on the orbit
                characteristics. A rotating solar array will always be capable
                to be optimally enlightened instead.
                <br />
                <br />
                <b>Battery recharge</b>
                For a deployable panel: the normal to the panel is aligned with
                the relative satellite-Sun position. This is the best condition
                for power generation.
                <br />
                For a satellite mounting panels on its surfaces: in this case we
                align the direction from which a maximum surface is seen with
                the relative satellite-sun position.
                <br />
                <br />
                <br />
                <br />
                <v-img
                  contain
                  :src="require('../../assets/images/tooltip-attitude.png')"
                  aspect-ratio="1.8"
                />
                <br />
                <br />
                <b>Additional attitude modes</b> For some missions (orbital
                transfer, RAAN phasing, orbit phasing and station keeping), an
                additional system can be selected in the spacecraft definition.
                This will activate the operational attitudes during the mission
                when the spacecraft goes over a ground station or an interest
                zone, defined by the user. Please refer to the spacecraft to
                find more information about these modes.
                <br />
                <br />
                <b>Attitude representation</b>
                Three angles are exported in order to describe the orientation
                of the spacecraft. These describe the rotation from a TNW
                orbital frame to the spacecraft frame.
                <br />
                <br />
                We recall the definition TNW frame:
                <br />
                <katex-element expression="X" />
                : unit vector in the direction of satellite's velocity vector.
                <br />
                <katex-element expression="Z" />
                : unit vector in the same direction as the orbit's angular
                momentum vector, that is, perpendicular to the orbit plane.
                <br />
                <katex-element expression="Y" />
                : unit vector chosen so that the (X,Y,Z) trihedral is direct.
                <katex-element expression="Y\wedge Z" />
                , points towards the velocity vector,
                <br />
                <br />
                Rotation angles around these axes are called yaw, pitch and roll
                respectively.
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-form-alert
              v-if="attitudeAndSystemSimulatedMessage !== ''"
              :type="attitudeAndSystemSimulatedMessageType"
            >
              {{ attitudeAndSystemSimulatedMessage }}
            </exo-form-alert>
            <exo-input-select
              :root="_self"
              v-if="
                inputs.attitudeAndSystemSimulated === true &&
                !this.operationSystemDefinedInSpacecraft
              "
              name="freeFlyingAttitudeMode"
            >
              <exo-help-dialog
                :title="getLabel('Coasting phases attitude mode')"
              >
                Attitude mode selected during coasting phases.
                <br />
                <br />
                <b>Recharge</b>: optimise the lighting recieved by the solar
                arrays, allowing for enhanced battery recharge.
                <br />
                <b>Payload</b>: points a specified axis towards nadir (see Shape
                section for setting). This mode can be used for simulating a
                nadir-pointing instrument.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-checkbox
              :disabled="eclipseOptionDisabled"
              :root="_self"
              name="withEclipse"
            >
              <exo-help-dialog title="Detect eclipses">
                When performing a numerical propagation a
                <em>conical shadow model</em> can be used to compute the eclipse
                intervals (during which the spacecraft is in the dark).
                <br />
                <br />
                There are several reasons that make this feature useful:<br />
                <ul>
                  <li>
                    Power budget estimation: it is fundamental to take eclipses
                    into account to correctly estimate the collected amount of
                    power;
                  </li>
                  <li>Plot the eclipses ephemerides.</li>
                </ul>
                If you are not in one of the use cases above, do not check this
                option to speed-up the simulation.<br />
                <br />
              </exo-help-dialog>
            </exo-input-checkbox>
            <div class="d-flex align-start">
              <v-btn
                v-if="inputs.linkBudgetSimulated"
                icon
                @click="editLinkBudget"
                class="mr-1"
              >
                <v-icon color="black">edit</v-icon>
              </v-btn>
              <exo-input-checkbox
                :root="_self"
                name="linkBudgetSimulated"
                :onChange="updateLinkBudgetModal"
              >
                <exo-help-dialog title="Link budget simulation">
                  <tooltipLinkBudget />
                </exo-help-dialog>
              </exo-input-checkbox>
            </div>
            <exo-form-alert
              type="info"
              v-if="this.inputs.linkBudgetSimulated === true"
            >
              When simulating link budget, the spacecraft must have an antenna
              with RF fields defined.
            </exo-form-alert>

            <v-dialog persistant max-width="800" v-model="showLinkBudgetModal">
              <v-stepper v-model="stepper">
                <v-stepper-header :style="{ 'background-color': '#3BE287' }">
                  <v-stepper-step color="black" :complete="stepper > 1" step="1"
                    >Groundstation</v-stepper-step
                  >

                  <v-divider></v-divider>

                  <v-stepper-step color="black" :complete="stepper > 2" step="2"
                    >Frequency Band</v-stepper-step
                  >

                  <v-divider></v-divider>

                  <v-stepper-step color="black" :complete="stepper > 3" step="3"
                    >Environmental Parameters</v-stepper-step
                  >

                  <v-divider></v-divider>

                  <v-stepper-step color="black" :complete="stepper > 4" step="4"
                    >Coding scheme</v-stepper-step
                  >
                </v-stepper-header>

                <v-stepper-items>
                  <v-stepper-content step="1">
                    <v-card class="mb-4 pb-3">
                      <exo-input-ref-multiple
                        :root="_self"
                        name="groundStations"
                        :isParametric="false"
                        type="groundStation"
                        componentRoute="/analyticalconstellationgroundstation"
                        notEmpty
                      />
                      <exo-form-alert type="info">
                        When simulating link budget, the selected ground
                        stations must have their RF fields defined.
                      </exo-form-alert>
                      <exo-input-select :root="_self" name="rfLinkType" />

                      <exo-input-double :root="_self" name="desiredDataRate">
                        <exo-help-dialog title="Data rate">
                          <p>
                            The data rate is used to compute the command system
                            "Energy per bit to noise power density ratio"
                            (Eb/N0), equivalent to the "Signal-to-Noise ratio",
                            and subsequently the link margin. This method
                            assumes the receiver uses a matched filter, and
                            assumes a spectral efficiency of 1 bps/Hz of
                            bandwidth, so that the bandwidth in Hz is equal to
                            the data rate in Hz.
                          </p>
                          <tooltipEquationEbN0 />
                        </exo-help-dialog>
                      </exo-input-double>
                    </v-card>
                  </v-stepper-content>

                  <v-stepper-content step="2">
                    <v-card class="mb-4 pb-3">
                      <exo-input-double :root="_self" name="uplinkFrequency">
                        <tooltipFrequency />
                      </exo-input-double>
                      <exo-input-double :root="_self" name="downlinkFrequency">
                        <tooltipFrequency />
                      </exo-input-double>
                    </v-card>
                  </v-stepper-content>

                  <v-stepper-content step="3">
                    <exo-input-double :root="_self" name="polarizationLoss">
                      <exo-help-dialog title="Polarization loss">
                        <p>
                          The antenna polarization refers to the alignment of
                          the radiated eletromagnetic field and their asymmetry.
                          The polarization loss is a combination of the axial
                          ratio of the transmitting and receiving antennas
                          (indicating that these antennas may handle more or
                          less power according to its rotation) and the
                          polarization angle between them (related to the
                          alignement of their rotations). For simplicity, you
                          may input directly the polarization loss associated to
                          the RF link.
                        </p>
                        <tooltipEquationEnvironmentalLosses />
                        <tooltipEquationEbN0 />
                      </exo-help-dialog>
                    </exo-input-double>
                    <exo-input-double :root="_self" name="rainLoss">
                      <exo-help-dialog title="Rain loss">
                        <p>
                          When the RF link passes through rain (or snow, ice,
                          storm, etc.), the quality of the signal can be
                          severely deteriorated, especially for frequencies
                          above 11 GHz. A common approach is to consider the
                          worst case loss (for example in 95 %, 99 %, 99.9 %,
                          etc. of the cases) for the selected regions of the
                          world, which can be found in communication textbooks.
                        </p>
                        <tooltipEquationEnvironmentalLosses />
                        <tooltipEquationEbN0 />
                      </exo-help-dialog>
                    </exo-input-double>
                  </v-stepper-content>

                  <v-stepper-content step="4">
                    <v-card class="mb-4 pb-3">
                      <exo-input-select :root="_self" name="modCodType">
                        <tooltipModCod />
                      </exo-input-select>
                      <exo-input-double
                        :root="_self"
                        name="customTheoreticalSignalToNoiseRatio"
                      >
                      </exo-input-double>
                      <exo-input-double :root="_self" name="customBitErrorRate">
                      </exo-input-double>
                      <exo-input-double
                        :root="_self"
                        name="implementationLossEstimate"
                      >
                      </exo-input-double>
                    </v-card>
                  </v-stepper-content>
                </v-stepper-items>

                <v-row class="px-6 pb-6">
                  <v-col>
                    <v-btn
                      elevation="0"
                      v-if="stepper > 1"
                      @click="stepper--"
                      color="#E1E1E1"
                      >Back</v-btn
                    >
                  </v-col>
                  <v-col class="text-right">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeLinkBudgetDialog" text>Cancel</v-btn>
                    <v-btn
                      v-if="stepper === 4"
                      elevation="0"
                      color="primary"
                      @click="
                        showLinkBudgetModal = false
                        linkBudgetDataComplete = true
                        stepper = 1
                      "
                      :disabled="!linkBudgetsCanProgressToNextStep"
                    >
                      <template v-if="linkBudgetDataComplete">
                        Confirm
                      </template>
                      <template v-else> Complete </template>
                    </v-btn>
                    <v-btn
                      v-else
                      elevation="0"
                      color="primary"
                      @click="stepper < 4 ? stepper++ : stepper"
                      :disabled="!linkBudgetsCanProgressToNextStep"
                    >
                      Continue
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper>
            </v-dialog>
          </exo-card-form>
          <exo-card-form label="Outputs">
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="ephemeridesRequested"
            >
              <exo-help-dialog title="Ephemerides generation">
                Several parameters can be stored all along the simulation for
                later use. These are:
                <br />
                <br />
                <b>Spacecraft motion representation</b>: keplerian, equinoctial,
                circular orbital elements, and cartesian coordinates (in CIRF
                inertial frame),
                <br />
                <b>Spacecraft system</b>: power collected by solar arrays, state
                of charge of the battery, electrical consumption,
                <br />
                <b>Spacecraft thrust</b>: total mass, fuel mass consumption,
                thrust direction
                <br />
                <b>Spacecraft attitude</b>: current attitude mode, orientation
                (rotation angles with respect to a TNW orbital frame),
                <br />
                <b>Spacecraft environment</b>: eclipse status (1 = spacecraft is
                in eclipse).
                <br />
                <br />
                <br />
                For more information on orbit representation check the
                corresponding help box, or the reference below.
                <b>References</b>
                <br />
                D.A. Vallado, Fundamentals of Astrodynamics and Applications,
                4th ed, 2014, Space Technology Library.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="ephemeridesFileTypesRequested"
            >
              <exo-help-dialog title="Ephemerides file types">
                <table>
                  <tbody>
                    <tr>
                      <td>CCSDS</td>
                      <td>&nbsp;</td>
                      <td>Consultative Committee for Space Data Systems</td>
                    </tr>
                    <tr>
                      <td>CIC</td>
                      <td>&nbsp;</td>
                      <td>Centre d'Ingénierie Concourante (CNES)</td>
                    </tr>
                  </tbody>
                </table>
              </exo-help-dialog>
            </exo-input-select>
            <v-row align="center" no-gutters>
              <v-col cols="4">
                <v-checkbox
                  v-if="isEphemeridesSelected"
                  :disabled="
                    !isNumericalPropagation ||
                    (inputs.ephemeridesOscChoice &&
                      !inputs.ephemeridesMeanChoice) ||
                    isOAPMission
                  "
                  v-on:change="updateEphemeridesChoices"
                  v-model="inputs.ephemeridesOscChoice"
                  :label="getFormLabel('ephemeridesOscChoice')"
                ></v-checkbox>
              </v-col>
              <v-col cols="3">
                <v-checkbox
                  v-if="isEphemeridesSelected"
                  :disabled="
                    !isNumericalPropagation ||
                    (!inputs.ephemeridesOscChoice &&
                      inputs.ephemeridesMeanChoice) ||
                    isOAPMission ||
                    !isPerturbationSelected
                  "
                  v-on:change="updateEphemeridesChoices"
                  v-model="inputs.ephemeridesMeanChoice"
                  :label="getFormLabel('ephemeridesMeanChoice')"
                ></v-checkbox>
              </v-col>
              <v-col cols="4">
                <v-checkbox
                  :disabled="!isNumericalPropagation"
                  v-model="inputs.ephemeridesThreedChoice"
                  :label="getFormLabel('ephemeridesThreedChoice')"
                ></v-checkbox>
              </v-col>
              <v-col cols="1" class="m-0 p-0">
                <exo-help-dialog title="Ephemerides types">
                  Perturbations dramatically change the spacecraft's trajectory
                  with respect to the idealized Keplerian description. Perturbed
                  motion can be seen as the sum of a slowly varying part
                  (resulting from <em>secular</em> and
                  <em>long-periodic</em> perturbations) and of a fast
                  contribution (the <em>short-periodic</em> terms for which the
                  period equals the orbital period).
                  <br />
                  <br />
                  <b>Osculating elements</b>: these are the instantaneous
                  orbital elements taking into account the effect of
                  perturbations acting on the spacecraft.
                  <br />
                  <b>Mean elements</b>: these elements account for secular and
                  long periodic variations only. This means that the short
                  periodic effects (less than the orbital period) of
                  perturbations are filtered out. Thus, mean elements change
                  slowly with time (except for the anomaly). Usually they can be
                  derived from analytical theories or by numerical procedures.
                  <br />
                  <br />
                  Notes:
                  <ol>
                    <li>
                      Mean elements ephemerides are obtained using Orekit's
                      implementation of the Draper Semi-analytical Satellite
                      Theory (DSST).
                    </li>
                    <li>
                      When the <em>numerical</em> propagation type has been
                      selected, the simulation is performed using osculating
                      elements, but the manoeuvres are based on the mean
                      elements. At the end of the simulation the osculating
                      elements are converted into mean elements if mean
                      ephemerides are required.
                    </li>
                    <li>
                      If the simulation is performed without considering
                      enviromental perturbations, only osculating ephemerides
                      are available
                    </li>
                  </ol>
                  <br />
                  <br />
                  <br />
                </exo-help-dialog>
              </v-col>
            </v-row>
            <exo-input-double
              :root="_self"
              v-if="isEphemeridesStepRequested"
              name="ephemeridesStepInSeconds"
            >
              <exo-help-dialog title="Ephemerides time step">
                Gap between two simulation steps in the generated data. A short
                period will give more details but will require more time to be
                generated, and may slow down the data processing in the
                dashboard.<br />
                A short period is useful to observe behaviours on few orbits,
                e.g. analyze electric system in eclipse.<br /><br />
                The amount of data generated depends on the simulation duration
                and the types of ephemerides requested.<br />
                An estimated ratio is evaluated before the simulation in
                comparison with the maximum amount of data which can be usually
                handled without significant loss of performances.<br />
                The effective amount of data generated can differ from the
                estimated one which is based on the maximum duration of the
                manoeuvre.<br />
              </exo-help-dialog>
            </exo-input-double>
            <exo-form-alert
              v-if="ephemeridesCount > 0"
              :type="ephemeridesCountFeedbackStyle"
            >
              {{ ephemeridesCountFeedbackMessage }}
            </exo-form-alert>
            <exo-form-alert
              v-if="ephemeridesWarningMessage !== ''"
              type="warning"
            >
              {{ ephemeridesWarningMessage }}
            </exo-form-alert>
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
    <v-col ref="NumericalMissionErrorDisplay"> </v-col>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import plotLayout from '../../mixins/plotLayout.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import moment from 'moment'
import thrustPattern from '@/components/Forms/ThrustPattern.vue'
import tooltipThrustAttitudeMode from '../../components/Tooltips/Simulations/tooltipThrustAttitudeMode'
import tooltipHypotheticalManeuver from '../../components/Tooltips/Simulations/tooltipHypotheticalManeuver'
import tooltipSmaTopBottomMargin from '../../components/Tooltips/Simulations/tooltipSmaTopBottomMargin'
import tooltipAlongTrackTolerance from '../../components/Tooltips/Simulations/tooltipAlongTrackTolerance'
import tooltipPerturbations from '../../components/Tooltips/tooltipPerturbations'
import tooltipLinkBudget from '../../components/Tooltips/Simulations/LinkBudget/tooltipLinkBudget.vue'
import tooltipFrequency from '../../components/Tooltips/Simulations/LinkBudget/tooltipFrequency.vue'
import tooltipEquationEbN0 from '../../components/Tooltips/Simulations/LinkBudget/tooltipEquationEbN0.vue'
import tooltipEquationEnvironmentalLosses from '../../components/Tooltips/Simulations/LinkBudget/tooltipEquationEnvironmentalLosses.vue'
import tooltipModCod from '../../components/Tooltips/Simulations/LinkBudget/tooltipModCod.vue'
const context = 'SimulationResult'

export default {
  name: 'numericalmission',

  mixins: [formMixin, numericalMixin, plotLayout],
  components: {
    thrustPattern,
    tooltipThrustAttitudeMode,
    tooltipHypotheticalManeuver,
    tooltipSmaTopBottomMargin,
    tooltipAlongTrackTolerance,
    tooltipPerturbations,
    tooltipLinkBudget,
    tooltipFrequency,
    tooltipEquationEbN0,
    tooltipEquationEnvironmentalLosses,
    tooltipModCod
  },
  computed: {
    ...mapGetters(['missions', 'currentMission']),

    targetDateBeforeStartDate() {
      if (this.inputs.targetDateDefinitionType !== 'TARGET_DATE') {
        return false // no message
      }
      let startDate = this.inputs.overloadOrbitDateTime
        ? this.inputs.missionDateTime
        : this.inputs.initialOrbit?.orbitDateTime
      const targetDate = this.inputs.targetDate

      if (moment(startDate).format('X') > moment(targetDate).format('X')) {
        return true
      } else {
        return false
      }
    },

    unitaryDutyCycle() {
      return this.inputs.orbitalDutyCycle === 100
    },
    isNotValidThrustCustomAxis() {
      return (
        Number(this.inputs.thrustCustomPointingAxisT) === 0 &&
        Number(this.inputs.thrustCustomPointingAxisN) === 0 &&
        Number(this.inputs.thrustCustomPointingAxisW) === 0
      )
    },
    isNotValidRestCustomAxis() {
      return (
        Number(this.inputs.restCustomPointingAxisT) === 0 &&
        Number(this.inputs.restCustomPointingAxisN) === 0 &&
        Number(this.inputs.restCustomPointingAxisW) === 0
      )
    },
    eclipseOptionDisabled() {
      return (
        this.isOAPMission ||
        !this.isNumericalPropagation ||
        this.inputs.attitudeAndSystemSimulated === true ||
        this.inputs.dynamicDutyCycleSimulated === true ||
        this.inputs.stopThrustInEclipse === true
      )
    },
    maxOrbitalDutyCycle() {
      const t = parseFloat(this.inputs.numberOfThrustOrbits)
      const r = parseFloat(this.inputs.numberOfRestOrbits)

      const maxMean = (this.maxMeanDutyCycle * (t + r)) / t
      const defaultValue = 100
      if (isNaN(maxMean)) {
        return defaultValue
      }
      return Math.min(defaultValue, maxMean)
    },
    maxMeanDutyCycle() {
      if (this.inputs.spacecraft && this.inputs.spacecraft.thruster) {
        const maxDutyCycle = dataTools.computeMaxDutyCycleWithWarmup(
          this.inputs.spacecraft.onBoardAveragePower,
          this.inputs.spacecraft.thruster.power,
          this.inputs.spacecraft.thruster.standbyPower,
          this.inputs.spacecraft.thruster.warmUpPower,
          this.warmupDutyCycle
        )
        if (maxDutyCycle !== '') {
          return maxDutyCycle
        }
      }
      return 100
    },
    warmupDutyCycle() {
      if (!this.inputs.initialOrbit) {
        return 0
      }
      const earthMU = 3.986004415e14
      const initialOrbitPeriod =
        2 *
        Math.PI *
        this.inputs.initialOrbit.sma *
        Math.sqrt(this.inputs.initialOrbit.sma / earthMU)
      const cycleDuration =
        (this.inputs.numberOfRestOrbits + this.inputs.numberOfThrustOrbits) *
        initialOrbitPeriod
      let thrustArcsNumberInt = 1
      if (this.inputs.thrustArcsNumber.value === 'ONE') {
        thrustArcsNumberInt = 1
      } else {
        thrustArcsNumberInt = 2
      }
      const arcsPerCycle =
        this.inputs.numberOfThrustOrbits * thrustArcsNumberInt
      return (
        (arcsPerCycle * this.inputs.spacecraft.thruster.warmUpDuration) /
        cycleDuration
      )
    },

    unconsistantThrustPattern() {
      let t = 1
      let r = 0
      let s = 0
      if (
        this.inputs.numberOfThrustOrbits != null &&
        !isNaN(this.inputs.numberOfThrustOrbits)
      ) {
        t = parseFloat(this.inputs.numberOfThrustOrbits)
      }
      if (
        this.inputs.numberOfRestOrbits != null &&
        !isNaN(this.inputs.numberOfRestOrbits)
      ) {
        r = parseFloat(this.inputs.numberOfRestOrbits)
      }
      if (
        this.inputs.numberOfShiftOrbits != null &&
        !isNaN(this.inputs.numberOfShiftOrbits)
      ) {
        s = parseFloat(this.inputs.numberOfShiftOrbits)
      }
      return s >= t + r
    },

    strategyId() {
      // depending on the mission
      if (this.inputs.type === 'Orbital Transfer') {
        let changeSemiMajorAxis = false
        let changeEccentricity = false
        let changeInclination = false
        changeSemiMajorAxis =
          this.inputs.deltaSemiMajorAxis != null &&
          !isNaN(this.inputs.deltaSemiMajorAxis) &&
          parseFloat(this.inputs.deltaSemiMajorAxis) !== 0
        changeEccentricity =
          this.inputs.deltaEccentricity != null &&
          !isNaN(this.inputs.deltaEccentricity) &&
          parseFloat(this.inputs.deltaEccentricity) !== 0
        changeInclination =
          this.inputs.deltaInclination != null &&
          !isNaN(this.inputs.deltaInclination) &&
          parseFloat(this.inputs.deltaInclination) !== 0
        return (
          'a=' +
          changeSemiMajorAxis +
          ',e=' +
          changeEccentricity +
          ',i=' +
          changeInclination
        )
      }
      return this.inputs.type
    },

    thrustArcsNumberItems() {
      let currentAvailableStrategies =
        this.maneuveringStrategies[this.strategyId]
      let enumItems = this.getPropertiesDescription()['thrustArcsNumber'].values
      if (currentAvailableStrategies != null) {
        enumItems = enumItems.filter((value) =>
          Object.keys(currentAvailableStrategies).includes(value.value)
        )
      }
      return enumItems
    },

    thrustArcsPositionItems() {
      let currentAvailableStrategies =
        this.maneuveringStrategies[this.strategyId]
      let enumItems =
        this.getPropertiesDescription()['thrustArcsPosition'].values
      if (
        currentAvailableStrategies != null &&
        currentAvailableStrategies[this.inputs.thrustArcsNumber] != null
      ) {
        enumItems = enumItems.filter((value) =>
          currentAvailableStrategies[this.inputs.thrustArcsNumber].includes(
            value.value
          )
        )
      }
      return enumItems
    },

    thrustAttitudeModeItems() {
      return this.getPropertiesDescription()['thrustAttitudeMode'].values
    },

    restAttitudeModeItems() {
      return this.getPropertiesDescription()['restAttitudeMode'].values
    },

    thrustArcsPositionItemsNotCustom() {
      return this.thrustArcsPositionItems.filter(
        (value) => value.value !== 'CUSTOM'
      )
    },

    disabledManoeuvringStrategy() {
      if (this.inputs.spacecraft != null) {
        if (
          this.inputs.spacecraft.thruster.propulsionSystemType === 'CHEMICAL'
        ) {
          return 'disableOnChemical'
        }
      }
      return '' // enable all
    },

    disabledPerturbationsConfiguration() {
      if (this.inputs.type === 'RAAN Phasing') {
        return 'disabledOnRAAN'
      }
      if (this.inputs.type === 'Orbit Phasing') {
        this.inputs.perturbations = this.inputs.perturbations.filter(
          (i) => i !== 'EARTH_POTENTIAL'
        )
        return 'disabledOnOrbitPhasing'
      }
      if (this.inputs.type === 'LEO Station Keeping') {
        if (this.inputs.toleranceType === 'ALONG_TRACK_TOLERANCE') {
          this.inputs.perturbations = this.inputs.perturbations.filter(
            (i) => i !== 'EARTH_POTENTIAL'
          )
          return 'disabledAndUncheckOnSKLEO'
        }
        return 'disabledOnSKLEO'
      }
      return '' // enable all
    },
    disableToleranceType() {
      if (
        this.inputs.spacecraft !== null &&
        this.inputs.spacecraft.spacecraftGeometry !== null &&
        this.inputs.spacecraft.spacecraftGeometry.type !== 'SPHERE'
      ) {
        return 'disabledOnNonSpherical'
      }
      return ''
    },

    isOAPMission() {
      return this.inputs.type === 'Maneuvering Strategy Design'
    },

    displayErrorEccentricityWithPerturbations() {
      return this.isPerturbationSelected && this.inputs.deltaEccentricity > 0
    },

    displayErrorEccentricityRAAN() {
      if (this.inputs.type === 'RAAN Phasing') {
        return (
          this.inputs.initialOrbit != null &&
          this.inputs.initialOrbit.eccentricity > 0.01
        )
      } else {
        return false
      }
    },

    displayErrorCoastingAltitude() {
      // Without Number(), the inputs would be compared as string (888>7777)
      return (
        Number(this.inputs.minCoastingAltitude) >
        Number(this.inputs.maxCoastingAltitude)
      )
    },

    displayErrorCoastingInclination() {
      // Without Number(), the inputs would be compared as string (888>7777)
      return (
        Number(this.inputs.minCoastingInclination) >
        Number(this.inputs.maxCoastingInclination)
      )
    },

    displayErrorTargetDeltaLtan() {
      return (
        this.isRequiredOrOptional('targetDeltaLtan', this.inputs) &&
        dataTools.convertGui2si('timeHMS', this.inputs.targetDeltaLtan) === 0
      )
    },

    isPerturbationSelected() {
      return (
        this.inputs.perturbations != null &&
        this.inputs.perturbations.length > 0
      )
    },

    isEphemeridesSelected() {
      return (
        this.inputs.ephemeridesRequested &&
        this.inputs.ephemeridesRequested.length > 0
      )
    },

    isEphemeridesFileTypesSelected() {
      return (
        this.inputs.ephemeridesFileTypesRequested &&
        this.inputs.ephemeridesFileTypesRequested.length > 0
      )
    },

    isEphemeridesStepRequested() {
      // for semi analytical, the step is automatic
      return (
        this.isNumericalPropagation &&
        (this.isEphemeridesSelected || this.isEphemeridesFileTypesSelected)
      )
    },

    expectedMaxDuration() {
      // for orbit extrapolation we could consider the case when target date provided
      let duration = 0
      for (let durationField of [
        'maximumDuration',
        'duration',
        'targetMissionDuration',
        'targetMissionDurationForAol'
      ]) {
        if (this.isRequiredOrOptional(durationField, this.inputs)) {
          duration = this.inputs[durationField]
        }
      }
      return duration
    },

    ephemeridesCount() {
      if (
        this.isEphemeridesStepRequested &&
        this.inputs.ephemeridesStepInSeconds > 0
      ) {
        let multiplyFactor =
          this.inputs.ephemeridesOscChoice && this.inputs.ephemeridesMeanChoice
            ? 2
            : 1

        return (
          multiplyFactor *
          Math.ceil(
            dataTools.convertGui2si(
              this.getFieldType('maximumDuration'),
              this.expectedMaxDuration
            ) / this.inputs.ephemeridesStepInSeconds
          )
        )
      } else {
        return 0
      }
    },

    ephemeridesData() {
      return (
        ((this.ephemeridesCount * this.ephemerisDataPercentage) / 3000) *
        100
      ).toFixed(2)
    },

    ephemerisDataPercentage() {
      return (
        this.ephemeridesNumberOfFieldsPerEphemeris /
        this.ephemeridesMaxNumberOfFieldsPerEphemeris
      ).toFixed(2)
    },

    ephemeridesCountFeedbackStyle() {
      return this.ephemeridesData < 80
        ? 'success'
        : this.ephemeridesData < 100
        ? 'warning'
        : 'error'
    },

    ephemeridesCountFeedbackMessage() {
      return (
        'Expecting around ' +
        this.ephemeridesCount +
        ' ephemerides data with ' +
        this.ephemeridesData +
        '% of maximum ephemerides data'
      )
    },

    ephemeridesWarningMessage() {
      if (
        this.ephemeridesCount > 0 &&
        this.inputs.ephemeridesMeanChoice &&
        this.inputs.ephemeridesStepInSeconds < 86400
      ) {
        return 'Mean ephemerides can be slow to be computed, the generation of results will take a long time if the time step is too small. You should consider a time step bigger than one day.'
      }
      return ''
    },

    ephemeridesMaxNumberOfFieldsPerEphemeris() {
      return Object.values(this.ephemeridesTypesCount).reduce(
        (acc, curr) => acc + curr,
        0
      )
    },

    ephemeridesNumberOfFieldsPerEphemeris() {
      if (!this.inputs.ephemeridesRequested) {
        return 0
      }
      return this.inputs.ephemeridesRequested.reduce(
        (acc, curr) => acc + this.ephemeridesTypesCount[curr],
        0
      )
    },

    attitudeAndSystemSimulatedMessageType() {
      return this.attitudeAndSystemSimulatedMessage.startsWith('Error')
        ? 'error'
        : 'warning'
    },

    attitudeAndSystemSimulatedMessage() {
      if (this.inputs.spacecraft != null) {
        if (
          this.inputs.attitudeAndSystemSimulated != null &&
          this.inputs.attitudeAndSystemSimulated !== ''
        ) {
          if (this.inputs.propagationType === 'SEMI_ANALYTICAL') {
            if (this.inputs.attitudeAndSystemSimulated) {
              return 'Error : the system can not be simulated with the semi-analytical propagation.'
            }
          } else {
            // numerical
            if (
              this.inputs.attitudeAndSystemSimulated &&
              !this.systemDefinedInSpacecraft
            ) {
              return 'Error : the spacecraft has no power system defined.'
            } else if (
              !this.inputs.attitudeAndSystemSimulated &&
              this.systemDefinedInSpacecraft
            ) {
              return 'Info : the selected spacecraft defines a power system but it is not used during the mission.'
            } else if (
              this.inputs.attitudeAndSystemSimulated &&
              (this.inputs.type === 'RAAN Phasing' ||
                this.inputs.type === 'Orbit Phasing')
            ) {
              return "Info : if the thrust duty cycle can not be respected due to the system, the target might not be met. The simulation mission 'Maneuvering Strategy Design' can be used to configure correctly the system."
            }
          }
        }
      }
      return ''
    },

    spacecraftShapeMessageType() {
      return this.spacecraftShapeMessage.startsWith('Error')
        ? 'error'
        : 'warning'
    },

    spacecraftShapeMessage() {
      if (
        this.inputs.spacecraft !== null &&
        this.inputs.spacecraft.spacecraftGeometry !== null &&
        this.inputs.type === 'LEO Station Keeping' &&
        this.inputs.spacecraft.spacecraftGeometry.type !== 'SPHERE'
      ) {
        return 'Warning : With your selected spacecraft shape you can not use the along-track tolerance. Use a spherical spacecraft shape instead.'
      }
      return ''
    },

    systemDefinedInSpacecraft() {
      return (
        this.inputs.spacecraft != null &&
        this.inputs.spacecraft.powerSystemDefined &&
        this.inputs.spacecraft.spacecraftGeometry &&
        this.inputs.spacecraft.spacecraftGeometry.type !== 'SPHERE'
      )
    },

    operationSystemDefinedInSpacecraft() {
      return this.inputs?.spacecraft?.operationSystemDefined
    },

    isNumericalPropagation() {
      return this.inputs.propagationType === 'NUMERICAL'
    },

    ephemerideListUpdaters() {
      return (
        'isNumericalPropagation:' +
        this.isNumericalPropagation +
        ',' +
        'withEclipse:' +
        this.inputs.withEclipse +
        ',' +
        'attitudeAndSystemSimulated:' +
        this.inputs.attitudeAndSystemSimulated +
        ',' +
        'dynamicDutyCycleSimulated:' +
        this.inputs.dynamicDutyCycleSimulated +
        ',' +
        'linkBudgetSimulated' +
        this.inputs.linkBudgetSimulated
      )
    },

    perturbationWatcher() {
      return this.inputs.perturbations
    },

    deltaOrbitalElementsReadOnly() {
      return this.inputs.targetDefinition === 'TARGET_ORBIT'
    },

    targetOrbitWatcher() {
      return this.inputs.targetOrbit
    },

    initialOrbitWatcher() {
      return this.inputs.initialOrbit
    },

    thrustArcDefinitionWatcher() {
      return [this.inputs.thrustArcsNumber, this.inputs.thrustArcDefinition]
    },

    targetDefinitionWatcher() {
      return this.inputs.targetDefinition
    },

    orbitalDutyCycleInitializerWatcher() {
      if (
        this.inputs.orbitalDutyCycle === '' ||
        this.inputs.orbitalDutyCycle == null ||
        this.inputs.orbitalDutyCycle === 0
      ) {
        return this.maxMeanDutyCycle
      }
      return 'done'
    },

    showUseTargetOrbitRaanOption() {
      return (
        this.validDefinitionByTargetOrbit &&
        this.inputs.raanPhasingReference === 'RENDEZ_VOUS' &&
        this.isRequiredOrOptional('targetRaanLtanType', this.inputs)
      )
    },

    linkBudgetsCanProgressToNextStep() {
      const fieldsToCheck = []
      if (this.stepper === 1) {
        fieldsToCheck.push('groundStations', 'desiredDataRate')
      } else if (this.stepper === 2) {
        fieldsToCheck.push('uplinkFrequency')
        fieldsToCheck.push('downlinkFrequency')
      } else if (this.stepper === 3 && this.showLinkBudgetCustomCodeFields) {
        fieldsToCheck.push(
          'customTheoreticalSignalToNoiseRatio',
          'customBitErrorRate'
        )
      }
      return fieldsToCheck.every((key) => {
        return this.getFieldState(
          this.inputs,
          key,
          this.isRequiredField(key, this.inputs)
        ).value
      })
    },

    showLinkBudgetCustomCodeFields() {
      return this.inputs.modCodType === 'CUSTOM'
    },

    useFinalOrbitRaanWatcher() {
      return this.useFinalOrbitRaan
    },

    validDefinitionByTargetOrbit() {
      return (
        this.inputs.targetDefinition === 'TARGET_ORBIT' &&
        this.inputs.targetOrbit != null &&
        this.inputs.initialOrbit != null
      )
    }
  },

  async mounted() {
    this.$root.$on('copy-mission', this.copyMissionHandler)
    this.$root.$on('create-mission', this.createMissionHandler)
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    await this.refreshOrbits()
    await this.refreshSpacecrafts()
    await this.refreshGroundStations()
    this.refreshForm()
    this.updateManeuveringStrategies()
    this.updateEphemeridesList()
  },

  beforeDestroy() {
    this.$root.$off('copy-mission', this.copyMissionHandler)
    this.$root.$off('create-mission', this.createMissionHandler)
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
  },

  watch: {
    unitaryDutyCycle(val) {
      if (val) {
        this.inputs.thrustArcsNumber = 'ONE'
        this.inputs.thrustArcsPosition = 'ASCENDING_NODE'
        this.inputs.numberOfShiftOrbits = 0
        this.inputs.thrustArcDuration = ''
      } else {
        this.inputs.thrustArcsNumber = 'TWO'
        this.inputs.thrustArcsPosition = 'ASCENDING_AND_DESCENDING_NODES'
        this.inputs.numberOfShiftOrbits = 0
      }
    },
    isNumericalPropagation(value) {
      if (!value) {
        this.inputs.ephemeridesThreedChoice = false
        this.inputs.withEclipse = false
        this.inputs.attitudeAndSystemSimulated = false
      }
      this.updateEphemeridesChoices()
    },
    ephemerideListUpdaters(value) {
      this.updateEphemeridesList()
    },

    perturbationWatcher(value) {
      this.updateEphemeridesChoices()
    },

    initialOrbitWatcher(value) {
      this.updateDeltaOrbitalElements()
      this.thrustArcDefinitionUpdated()
    },

    thrustArcDefinitionWatcher(value) {
      this.thrustArcDefinitionUpdated()
    },

    targetOrbitWatcher(value) {
      this.updateDeltaOrbitalElements()
    },

    targetDefinitionWatcher(value) {
      this.updateDeltaOrbitalElements()
    },

    orbitalDutyCycleInitializerWatcher(value) {
      this.updateDeltaOrbitalElements()
      if (value !== 'done') {
        this.inputs.orbitalDutyCycle = this.maxMeanDutyCycle
      }
    },

    useFinalOrbitRaanWatcher(value) {
      this.updateDeltaOrbitalElements()
    },

    showUseTargetOrbitRaanOption(value) {
      if (value === false) {
        this.useFinalOrbitRaan = false
      }
    }
  },

  data() {
    return {
      tableFields: [
        { key: 'displayedName', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'spacecraft', label: 'Spacecraft', sortable: true },
        { key: 'payload', label: 'Payload', sortable: true },
        { key: 'antenna', label: 'Antenna', sortable: true },
        { key: 'initialOrbit', label: 'Initial orbit', sortable: true },
        {
          key: 'simulationDuration',
          label:
            'Mission duration' +
            this.getResultDefaultUnitLabel('simulationDuration'),
          sortable: true
        },
        {
          key: 'totalDeltaV',
          label: 'ΔV' + this.getResultDefaultUnitLabel('totalDeltaV'),
          sortable: true
        },
        {
          key: 'totalConsumption',
          label:
            'Used propellant' +
            this.getResultDefaultUnitLabel('totalConsumption')
        },
        { key: 'results', label: 'Result' },
        { key: 'actions', label: 'Actions' }
      ],
      ephemeridesTypesCount: {
        CARTESIAN: 6,
        CIRCULAR: 8,
        EQUINOCTIAL: 8,
        KEPLERIAN: 8,
        SYSTEM: 1,
        ATTITUDE: 3,
        ECLIPSE: 1,
        THRUST: 5,
        MANOEUVRING_STRATEGIES: 1,
        LINK_BUDGET: 2
      },

      orbitsNotParametrics: [],
      spacecraftsNotParametrics: [],
      groundStationsNotParametrics: [],
      enumPropertiesList: this.getEnumProperties(),

      missionPlot: null,
      modalRef: { content: [] },
      showLinkBudgetModal: false,
      maneuveringStrategies: {},

      errorThrustDuration: '',

      useFinalOrbitRaan: false,

      inputs: {
        initialOrbit: null,
        targetOrbit: null,
        type: this.getDefaultValue('type'),
        targetDefinition: this.getDefaultValue('targetDefinition'),
        optimizationType: this.getDefaultValue('optimizationType'),
        raanPhasingReference: this.getDefaultValue('raanPhasingReference'),
        orbitPhasingOptimizationType: this.getDefaultValue(
          'orbitPhasingOptimizationType'
        ),
        spacecraft: null,
        missionDateTime: this.getDefaultValue('missionDateTime'),
        maximumDuration: this.getDefaultValue('maximumDuration'),
        duration: this.getDefaultValue('duration'),
        propagationType: this.getDefaultValue('propagationType'),
        deltaInclination: this.getDefaultValue('deltaInclination'),
        deltaEccentricity: this.getDefaultValue('deltaEccentricity'),
        deltaSemiMajorAxis: this.getDefaultValue('deltaSemiMajorAxis'),
        perturbations: this.getDefaultValue('perturbations'),
        customSolarFlux: this.getDefaultValue('customSolarFlux'),
        earthPotentialConfiguration: this.getDefaultValue(
          'earthPotentialConfiguration'
        ),
        customEarthPotentialOrder: this.getDefaultValue(
          'customEarthPotentialOrder'
        ),
        customEarthPotentialDegree: this.getDefaultValue(
          'customEarthPotentialDegree'
        ),
        coastingDomain: this.getDefaultValue('coastingDomain'),
        minCoastingAltitude: this.getDefaultValue('minCoastingAltitude'),
        maxCoastingAltitude: this.getDefaultValue('maxCoastingAltitude'),
        minCoastingInclination: this.getDefaultValue('minCoastingInclination'),
        maxCoastingInclination: this.getDefaultValue('maxCoastingInclination'),
        targetMissionDuration: this.getDefaultValue('targetMissionDuration'),
        targetMissionDurationForAol: this.getDefaultValue(
          'targetMissionDurationForAol'
        ),
        targetDeltaRaan: this.getDefaultValue('targetDeltaRaan'),
        targetDeltaLtan: this.getDefaultValue('targetDeltaLtan'),
        targetRaanLtanType: this.getDefaultValue('targetRaanLtanType'),
        targetDeltaVmax: this.getDefaultValue('targetDeltaVmax'),
        targetDeltaArgumentOfLatitude: this.getDefaultValue(
          'targetDeltaArgumentOfLatitude'
        ),
        thrustArcsNumber: this.getDefaultValue('thrustArcsNumber'),
        thrustArcDefinition: this.getDefaultValue('thrustArcDefinition'),
        thrustArcDuration: this.getDefaultValue('thrustArcDuration'),
        maneuveringStrategyChoice: this.getDefaultValue(
          'maneuveringStrategyChoice'
        ),
        thrustArcsPosition: this.getDefaultValue('thrustArcsPosition'),
        numberOfThrustOrbits: this.getDefaultValue('numberOfThrustOrbits'),
        numberOfRestOrbits: this.getDefaultValue('numberOfRestOrbits'),
        numberOfShiftOrbits: this.getDefaultValue('numberOfShiftOrbits'),
        stopThrustInEclipse: this.getDefaultValue('stopThrustInEclipse'),
        dynamicDutyCycleSimulated: this.getDefaultValue(
          'dynamicDutyCycleSimulated'
        ),
        orbitalDutyCycle: this.getDefaultValue('orbitalDutyCycle'),
        reference: this.getDefaultValue('reference'),
        deltaMeanLongitudeArgument: this.getDefaultValue(
          'deltaMeanLongitudeArgument'
        ),
        thrustArcMeanLongitudeArgument: this.getDefaultValue(
          'thrustArcMeanLongitudeArgument'
        ),
        ephemeridesOscChoice: this.getDefaultValue('ephemeridesOscChoice'),
        ephemeridesMeanChoice: this.getDefaultValue('ephemeridesMeanChoice'),
        ephemeridesThreedChoice: this.getDefaultValue(
          'ephemeridesThreedChoice'
        ),
        ephemeridesRequested: this.getDefaultValue('ephemeridesRequested'),
        ephemeridesStepInSeconds: this.getDefaultValue(
          'ephemeridesStepInSeconds'
        ),
        overloadOrbitDateTime: this.getDefaultValue('overloadOrbitDateTime'),
        thrustAttitudeMode: this.getDefaultValue('thrustAttitudeMode'),
        restAttitudeMode: this.getDefaultValue('restAttitudeMode'),
        thrustCustomPointingAxisT: this.getDefaultValue(
          'thrustCustomPointingAxisT'
        ),
        thrustCustomPointingAxisN: this.getDefaultValue(
          'thrustCustomPointingAxisN'
        ),
        thrustCustomPointingAxisW: this.getDefaultValue(
          'thrustCustomPointingAxisW'
        ),
        customManeuver: this.getDefaultValue('customManeuver'),
        maneuverDeltaSma: this.getDefaultValue('maneuverDeltaSma'),
        maneuverDeltaInc: this.getDefaultValue('maneuverDeltaInc'),
        maneuverDeltaEcc: this.getDefaultValue('maneuverDeltaEcc'),
        restCustomPointingAxisT: this.getDefaultValue(
          'restCustomPointingAxisT'
        ),
        restCustomPointingAxisN: this.getDefaultValue(
          'restCustomPointingAxisN'
        ),
        restCustomPointingAxisW: this.getDefaultValue(
          'restCustomPointingAxisW'
        ),
        withEclipse: this.getDefaultValue('withEclipse'),
        attitudeAndSystemSimulated: this.getDefaultValue(
          'attitudeAndSystemSimulated'
        ),
        linkBudgetSimulated: this.getDefaultValue('linkBudgetSimulated'),
        freeFlyingAttitudeMode: this.getDefaultValue('freeFlyingAttitudeMode'),
        layout: this.getDefaultValue('layout'), // to save the dashboard, should be optional in fieldsdescription to be automatically copied
        smaTopMargin: this.getDefaultValue('smaTopMargin'),
        smaBottomMargin: this.getDefaultValue('smaBottomMargin'),
        Payload: null,
        targetDateDefinitionType: this.getDefaultValue(
          'targetDateDefinitionType'
        ),
        targetDate: this.getDefaultValue('targetDate'),
        toleranceType: this.getDefaultValue('toleranceType'),
        alongTrackTolerance: this.getDefaultValue('alongTrackTolerance'),
        groundStations: this.getDefaultValue('groundStations'),
        rfLinkType: this.getDefaultValue('rfLinkType'),
        desiredDataRate: this.getDefaultValue('desiredDataRate'),
        uplinkFrequency: this.getDefaultValue('uplinkFrequency'),
        downlinkFrequency: this.getDefaultValue('downlinkFrequency'),
        polarizationLoss: this.getDefaultValue('polarizationLoss'),
        rainLoss: this.getDefaultValue('rainLoss'),
        modCodType: this.getDefaultValue('modCodType'),
        customTheoreticalSignalToNoiseRatio: this.getDefaultValue(
          'customTheoreticalSignalToNoiseRatio'
        ),
        customBitErrorRate: this.getDefaultValue('customBitErrorRate'),
        implementationLossEstimate: this.getDefaultValue(
          'implementationLossEstimate'
        )
      },

      stepper: 1,
      linkBudgetDataComplete: false,
      linkBudgetExistingData: {},

      modalInfo: { title: '', content: '', config: {} },
      modalError: { title: '', content: '' },
      displayed: null
    }
  },

  methods: {
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    thrustArcDefinitionUpdated() {
      this.thrustArcDurationUpdate()
      this.dutyCycleUpdate()
    },

    thrustArcDurationUpdate() {
      if (this.inputs.thrustArcDefinition === 'THRUST_DURATION') {
        this.errorThrustDuration = ''
        const duration2DutycycleRequest = {
          orbit: this.inputs.initialOrbit,
          thrustArcsNumber: this.inputs.thrustArcsNumber,
          thrustArcDuration: dataTools.convertGui2si(
            this.getFieldType('thrustArcDuration'),
            this.inputs.thrustArcDuration
          )
        }
        this.$http
          .post('/physics/duration2dutycycle', duration2DutycycleRequest)
          .then((response) => {
            const expectedDutyCycle = dataTools.convertSi2Display(
              this.getFieldType('orbitalDutyCycle'),
              response.data.dutyCycle
            )
            if (expectedDutyCycle !== '' && expectedDutyCycle != null) {
              if (expectedDutyCycle < 100) {
                this.inputs.orbitalDutyCycle = expectedDutyCycle
              } else {
                this.errorThrustDuration =
                  'The thrust duration requested is more than the orbital period'
              }
            }
          })
          .catch((e) => {
            this.inputs.orbitalDutyCycle = null
          })
      }
    },

    dutyCycleUpdate() {
      if (this.inputs.thrustArcDefinition === 'DUTY_CYCLE') {
        this.errorThrustDuration = ''
        if (this.inputs.orbitalDutyCycle === 100) {
          this.inputs.thrustArcDuration = ''
          return
        }
        const duration2DutycycleRequest = {
          orbit: this.inputs.initialOrbit,
          thrustArcsNumber: this.inputs.thrustArcsNumber,
          dutyCycle: dataTools.convertGui2si(
            this.getFieldType('orbitalDutyCycle'),
            this.inputs.orbitalDutyCycle
          )
        }
        this.$http
          .post('/physics/duration2dutycycle', duration2DutycycleRequest)
          .then((response) => {
            const thrustArcDuration = response.data.thrustArcDuration
            if (thrustArcDuration !== '' && thrustArcDuration != null) {
              this.inputs.thrustArcDuration = dataTools.convertSi2Display(
                this.getFieldType('thrustArcDuration'),
                response.data.thrustArcDuration
              )
            }
          })
          .catch((e) => {
            this.inputs.thrustArcDuration = ''
          })
      }
    },

    // TODO wathcer on and thrustArcsNumber & thrustArcDefinition to call thrustArcDurationUpdate & dutyCycleUpdate

    updateManeuveringStrategies() {
      Vue.axios.get('/physics/strategies').then((response) => {
        if (response.data !== null) {
          this.maneuveringStrategies = response.data
        }
      })
    },

    updateDeltaOrbitalElements(value) {
      if (this.validDefinitionByTargetOrbit) {
        if (this.useFinalOrbitRaan) {
          this.updateDeltaOrbitalElement(
            'targetRaanLtanType',
            'targetDeltaLtan',
            'ltan'
          )
          this.updateDeltaOrbitalElement(
            'targetRaanLtanType',
            'targetDeltaRaan',
            'raan'
          )
        }
        this.updateDeltaOrbitalElement(
          'deltaInclination',
          'deltaInclination',
          'inclination'
        )
        this.updateDeltaOrbitalElement(
          'deltaEccentricity',
          'deltaEccentricity',
          'eccentricity'
        )
        this.updateDeltaOrbitalElement(
          'deltaSemiMajorAxis',
          'deltaSemiMajorAxis',
          'sma'
        )
      }
    },

    updateDeltaOrbitalElement(conditionField, inputName, orbitField) {
      if (this.isRequiredOrOptional(conditionField, this.inputs)) {
        let delta =
          this.inputs.targetOrbit[orbitField] -
          this.inputs.initialOrbit[orbitField]
        const fieldDesc = this.getPropertiesDescription()[inputName]
        const minInclusive = dataTools.convertPiToNumber(fieldDesc.minInclusive)
        const maxInclusive = dataTools.convertPiToNumber(fieldDesc.maxInclusive)
        if (
          dataTools.isNumber(minInclusive) &&
          dataTools.isNumber(maxInclusive)
        ) {
          const range = maxInclusive - minInclusive
          if (delta > maxInclusive) {
            delta = delta - range
          }
          if (delta < minInclusive) {
            delta = delta + range
          }
        }
        this.inputs[inputName] = dataTools.convertSi2gui(
          this.getFieldType(inputName),
          delta
        )
      }
    },

    updateEclipseFlag() {
      // when the user simulate the system, the eclipse detection becomes very useful
      if (
        this.inputs.attitudeAndSystemSimulated === true ||
        this.inputs.stopThrustInEclipse === true ||
        this.inputs.dynamicDutyCycleSimulated === true
      ) {
        this.inputs.withEclipse = true
      }
    },

    disableEphemeride(ephemerideToRemove) {
      this.enumPropertiesList.ephemeridesRequested =
        this.enumPropertiesList.ephemeridesRequested.filter(
          (ephem) => ephem.value !== ephemerideToRemove
        )
      this.inputs.ephemeridesRequested =
        this.inputs.ephemeridesRequested.filter(
          (ephem) => ephem !== ephemerideToRemove
        )
    },

    updateEphemeridesList() {
      this.enumPropertiesList = this.getEnumProperties() // initialise
      if (!this.inputs.withEclipse) {
        this.disableEphemeride('ECLIPSE')
      }
      if (!this.isNumericalPropagation) {
        this.disableEphemeride('THRUST')
        this.disableEphemeride('SYSTEM')
        this.disableEphemeride('ECLIPSE')
        this.disableEphemeride('ATTITUDE')
      }
      if (!this.inputs.attitudeAndSystemSimulated) {
        this.disableEphemeride('SYSTEM')
        this.disableEphemeride('ATTITUDE')
      }
      if (!this.inputs.linkBudgetSimulated) {
        this.disableEphemeride('LINK_BUDGET')
      }
      if (!this.inputs.dynamicDutyCycleSimulated) {
        this.disableEphemeride('MANOEUVRING_STRATEGIES')
      }
      this.disableEphemeride('QUATERNIONS')
      this.disableEphemeride('EULER_ANGLES')
    },

    updateEphemeridesChoices() {
      if (this.inputs.perturbations.indexOf('DRAG') === -1) {
        this.inputs.customSolarFlux = this.getDefaultValue('customSolarFlux')
      }
      if (!this.isNumericalPropagation) {
        this.inputs.ephemeridesOscChoice = false
        this.inputs.ephemeridesMeanChoice = true
      } else {
        if (!this.isPerturbationSelected) {
          // no perturbation : osc = mean => no conversion
          this.inputs.ephemeridesOscChoice = true
          this.inputs.ephemeridesMeanChoice = false
        }
      }
    },

    refreshTable() {},

    getDateTimeString() {
      let date = new Date(this.inputs.initialOrbit.orbitDateTime)
      return date.toUTCString()
    },

    getCurrentFormTitle() {
      const label = dataTools.getTypeLabel(
        this.getCurrentFormType(),
        this.inputs.type
      )
      return label + ' Simulation'
    },

    createMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.inputs.type = mission.type
        // set default configuration
        switch (mission.type) {
          case 'Orbital Transfer':
            this.inputs.ephemeridesRequested = ['KEPLERIAN']
            this.inputs.ephemeridesStepInSeconds = 3600
            break
          case 'LEO Station Keeping':
            this.inputs.ephemeridesRequested = ['KEPLERIAN']
            this.inputs.ephemeridesStepInSeconds = 3600
            this.inputs.perturbations = ['EARTH_POTENTIAL', 'DRAG']
            break
          case 'RAAN Phasing':
            this.inputs.ephemeridesRequested = ['KEPLERIAN']
            this.inputs.ephemeridesStepInSeconds = 3600
            this.inputs.perturbations = ['EARTH_POTENTIAL', 'DRAG']
            break
          case 'Orbit Phasing':
            this.inputs.ephemeridesRequested = ['KEPLERIAN']
            this.inputs.ephemeridesStepInSeconds = 3600
            break
          case 'Orbit Extrapolation':
            this.inputs.ephemeridesRequested = ['KEPLERIAN']
            this.inputs.ephemeridesStepInSeconds = 86400
            this.inputs.ephemeridesOscChoice = false
            this.inputs.ephemeridesMeanChoice = true
            this.inputs.perturbations = [
              'EARTH_POTENTIAL',
              'SRP',
              'THIRD_BODY',
              'DRAG'
            ]
            this.inputs.propagationType = 'SEMI_ANALYTICAL'
            break
          case 'Maneuvering Strategy Design':
            this.inputs.attitudeAndSystemSimulated = true
            this.inputs.ephemeridesRequested = ['SYSTEM', 'ECLIPSE', 'ATTITUDE']
            this.inputs.ephemeridesStepInSeconds = 30
            this.inputs.withEclipse = true
            break
          default:
            break
        }
        this.sendFormToServer()
      }
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
        }
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          const fieldsToUpdate = [
            this.orbitsNotParametrics,
            this.spacecraftsNotParametrics,
            this.groundStationsNotParametrics
          ]
          missionTools.updateAllReferences(this, fieldsToUpdate)
          this.inputs.layout = this.currentMission.layout // might be saved from dashboard
        }
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    // redefine from form.js to keep the form open on the upadted data
    onSave(evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({
            path:
              '/' +
              missionTools.missionUrlMapping[response.data.typeName].dashboard
          })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    getResultFieldType(field) {
      return dataTools.getFieldTypeFromPath(
        ['maneuverReport', field],
        context,
        null
      )
    },

    getResultDefaultUnitLabel(field) {
      let fieldType = this.getResultFieldType(field)
      return (
        ' ' + dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
      )
    },

    // /////////////// TODO should be merged with mission ///////////
    convertToArray(elements) {
      if (!Array.isArray(elements)) {
        return [elements]
      } else {
        return elements
      }
    },

    removeParameter(index) {
      this.configuration.targets.splice(index, 1)
    },

    mergeString(inputElements, fieldPath) {
      let uniqueVals = dataTools.getUniqueValues(
        this.convertToArray(inputElements),
        fieldPath
      )
      if (uniqueVals.length === 1) {
        return uniqueVals[0]
      } else {
        return uniqueVals
      }
    },

    isStringInside(expected, inputElements, fieldPath) {
      let elements = this.convertToArray(inputElements)
      return (
        elements.filter(
          (element) => dataTools.getValue(element, fieldPath) === expected
        ).length > 0
      ) // TODO MFI stop on first would be better
    },

    // Methods to manage result popup
    info(mission, button) {
      this.modalInfo.title = mission.name
      this.modalInfo.content = [mission]
      this.modalInfo.errorLog = mission.computationLog
      this.$root.$emit('bv::show::modal', 'modalInfo', button)
    },

    resetModal() {
      this.modalInfo.title = ''
      this.modalInfo.content = ''
      this.modalError.title = ''
      this.modalError.content = ''
      this.modalInfo.errorLog = ''
    },

    // TODO MFI delete ?
    displayErrorLog(mission, button) {
      this.modalError.title = mission.name
      this.modalError.content = mission.computationLog
      this.$root.$emit('bv::show::modal', 'modalError', button)
    },

    // Methods to compute &  export result
    displayPlot(mission) {
      if (!mission) {
        this.$refs.plotMission.setMission(null, null, null)
        return
      }
      if (this.displayed === null || this.displayed === '3d') {
        this.$refs.three.setMission(null, null, null)
        this.displayed = 'plot'
      }
      this.missionPlot = mission
      this.$refs.plotMission.setMission(
        mission,
        mission.results.ephemerides,
        mission.results.fieldIndexes
      )
    },

    display3d(mission) {
      if (!mission) {
        this.$refs.three.setMission(null, null, null)
        return
      }
      if (this.displayed === null || this.displayed === 'plot') {
        this.$refs.plotMission.setMission(null, null, null)
        this.displayed = '3d'
      }
      this.missionPlot = mission
      this.$refs.three.setMission(
        mission,
        mission.results.ephemerides,
        mission.results.fieldIndexes
      )
    },

    setMissionConfiguration(config) {
      this.missionPlot.plotConfiguration = JSON.parse(JSON.stringify(config)) // copy values, not ref
    },

    hide3d() {
      this.display3d(null)
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true // reset computation
      return baseObject
    },

    // methods to mange form & table like other components

    getCurrentFormType() {
      return 'SimulationRequest'
    },

    async refreshSpacecrafts() {
      let self = this
      await this.$http
        .get(this.getAPI('spacecraftdefinitions'))
        .then((response) => {
          self.spacecraftsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of spacecrafts. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    async refreshOrbits() {
      let self = this
      await this.$http
        .get(this.getAPI('orbitdefinitions'))
        .then((response) => {
          self.orbitsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of orbits. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'initialOrbit' || param === 'targetOrbit') {
        return this.orbits
      } else if (param === 'spacecraft') {
        return this.spacecrafts
      }
    },

    getMissionType(mission) {
      let types = { type: mission.type }
      if (mission.type === 'RAAN Phasing') {
        types.optimizationType = mission.optimizationType
        types.targetRaanLtanType = mission.targetRaanLtanType
        types.coastingDomain = mission.coastingDomain
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbit Phasing') {
        types.orbitPhasingOptimizationType =
          mission.orbitPhasingOptimizationType
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbital Transfer') {
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'LEO Station Keeping') {
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice

        types.toleranceType = mission.toleranceType
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbit Extrapolation') {
        types.targetDateDefinitionType = mission.targetDateDefinitionType
        types.modCodType = mission.modCodType
        types.rfLinkType = mission.rfLinkType
      }
      if (mission.type === 'Maneuvering Strategy Design') {
        types.restAttitudeMode = mission.restAttitudeMode
        types.thrustAttitudeMode = mission.thrustAttitudeMode
      }
      types.earthPotentialConfiguration = mission.earthPotentialConfiguration
      return types
    },

    getRequiredPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'required'
      )
      if (
        this.isOAPMission &&
        this.inputs.thrustAttitudeMode === 'THRUST_CUSTOM'
      ) {
        if (this.inputs.customManeuver) {
          requiredList.push('maneuverDeltaSma')
          requiredList.push('maneuverDeltaInc')
          requiredList.push('maneuverDeltaEcc')
        } else {
          requiredList.push('thrustCustomPointingAxisT')
          requiredList.push('thrustCustomPointingAxisN')
          requiredList.push('thrustCustomPointingAxisW')
        }
      }
      return requiredList
    },

    getOptionalPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    updateLinkBudgetModal() {
      if (this.inputs.linkBudgetSimulated === true) {
        this.showLinkBudgetModal = true
      }
    },

    editLinkBudget() {
      const {
        groundStations,
        desiredDataRate,
        rfLinkType,
        uplinkFrequency,
        downlinkFrequency,
        polarizationLoss,
        rainLoss,
        modCodType,
        customTheoreticalSignalToNoiseRatio,
        customBitErrorRate,
        implementationLossEstimate
      } = this.inputs
      const copy = {
        groundStations,
        desiredDataRate,
        rfLinkType,
        uplinkFrequency,
        downlinkFrequency,
        polarizationLoss,
        rainLoss,
        modCodType,
        customTheoreticalSignalToNoiseRatio,
        customBitErrorRate,
        implementationLossEstimate
      }
      this.linkBudgetExistingData = { ...copy }
      this.showLinkBudgetModal = true
      this.stepper = 1
    },

    closeLinkBudgetDialog() {
      if (this.linkBudgetDataComplete) {
        Object.keys(this.linkBudgetExistingData).forEach((key) => {
          this.inputs[key] = this.linkBudgetExistingData[key]
        })
      }
      this.showLinkBudgetModal = false
      this.stepper = 1
    },

    // ---------------------- field checks -----------------

    getFieldState(parentObject, fieldName, required) {
      let fieldState = numericalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      // Check if eccentricity target is inside [0;1[
      if (
        fieldState.value === true &&
        ((fieldName === 'targetDefinition' &&
          parentObject[fieldName] === 'TARGET_ORBIT' &&
          this.inputs.targetOrbit != null) ||
          (fieldName === 'deltaEccentricity' &&
            this.inputs.targetDefinition === 'DELTA' &&
            this.inputs.deltaEccentricity != null)) &&
        this.inputs.initialOrbit != null
      ) {
        let targetEcc
        let deltaEcc
        if (this.inputs.targetDefinition === 'TARGET_ORBIT') {
          deltaEcc =
            parseFloat(this.inputs.targetOrbit['eccentricity']) -
            parseFloat(this.inputs.initialOrbit['eccentricity'])
          targetEcc = parseFloat(this.inputs.targetOrbit['eccentricity'])
        } else {
          deltaEcc = parseFloat(this.inputs['deltaEccentricity'])
          targetEcc =
            parseFloat(this.inputs.initialOrbit['eccentricity']) +
            parseFloat(this.inputs['deltaEccentricity'])
        }

        if (deltaEcc !== 0 && (targetEcc < 0 || targetEcc >= 1)) {
          fieldState.value = false
          fieldState.message =
            'Sorry, if an eccentric maneuver is commanded, eccentricity target must be between [0;1[. Requested eccentricity target: ' +
            targetEcc +
            '. Please select a target orbit in the right range.'
        }
      }

      // Check if inclination target is inside [0;pi[
      if (
        fieldState.value === true &&
        ((fieldName === 'targetDefinition' &&
          parentObject[fieldName] === 'TARGET_ORBIT' &&
          this.inputs.targetOrbit != null) ||
          (fieldName === 'deltaInclination' &&
            this.inputs.targetDefinition === 'DELTA' &&
            this.inputs.deltaInclination != null)) &&
        this.inputs.initialOrbit != null
      ) {
        let targetInc
        let deltaInc
        if (this.inputs.targetDefinition === 'TARGET_ORBIT') {
          deltaInc =
            dataTools.rad2deg(
              parseFloat(this.inputs.targetOrbit['inclination'])
            ) -
            dataTools.rad2deg(
              parseFloat(this.inputs.initialOrbit['inclination'])
            )
          targetInc = dataTools.rad2deg(
            parseFloat(this.inputs.targetOrbit['inclination'])
          )
        } else {
          deltaInc = parseFloat(this.inputs['deltaInclination'])
          targetInc =
            dataTools.rad2deg(
              parseFloat(this.inputs.initialOrbit['inclination'])
            ) + parseFloat(this.inputs['deltaInclination'])
        }

        if (deltaInc !== 0 && (targetInc < 0 || targetInc > 180)) {
          fieldState.value = false
          fieldState.message =
            'Sorry, if an inclination maneuver is commanded, inclination target must be between [0;180] deg. Requested inclination target: ' +
            targetInc +
            '. Please select a target orbit in the right range.'
        }
      }

      //check if custom earth potential max order <= degree
      if (
        fieldState.value === true &&
        fieldName === 'customEarthPotentialOrder' &&
        this.inputs.perturbations?.includes('EARTH_POTENTIAL') &&
        this.isRequiredOrOptional('customEarthPotentialOrder', this.inputs)
      ) {
        let order = parseInt(this.inputs['customEarthPotentialOrder'])
        let degree = parseInt(this.inputs['customEarthPotentialDegree'])
        if (order > degree) {
          fieldState.value = false
          fieldState.message =
            'The custom maximum order cannot be greater than the maximum degree.'
        }
      }

      // check if any grounds stations were selected for link budget
      if (
        fieldName === 'groundStations' &&
        this.inputs.linkBudgetSimulated === true &&
        (this.inputs.groundStations == null ||
          this.inputs.groundStations.length === 0)
      ) {
        fieldState.value = false
        fieldState.message =
          'At least one ground station must be selected for simulating link budget.'
      }

      // check if station-keeping orbit is low enough
      const checkTopMargin =
        fieldName === 'smaTopMargin' &&
        this.isRequiredOrOptional('smaTopMargin', this.inputs)
      if (
        fieldState.value === true &&
        this.inputs.type === 'LEO Station Keeping' &&
        (fieldName === 'initialOrbit' || checkTopMargin) &&
        this.inputs.initialOrbit != null
      ) {
        const topMargin = this.isRequiredOrOptional('smaTopMargin', this.inputs)
          ? parseFloat(this.inputs['smaTopMargin'])
          : 0
        const apogeeAltitude = parseFloat(
          this.inputs['initialOrbit'].apogeeAltitude
        )
        const maxAltitudeReached = apogeeAltitude / 1e3 + topMargin
        if (maxAltitudeReached > 900) {
          fieldState.value = false
          fieldState.message =
            'Maximum altitude reached must be lower than 900 km (current is ' +
            maxAltitudeReached +
            ' km)'
        }
      }

      return fieldState
    },

    // duplicated for special check on perturbations
    getEnumFieldState(parentObject, fieldName, required) {
      let state = {
        value: true, // do not initialize with null, it means field empty
        message: true
      }
      if (parentObject[fieldName] == null && !required) {
        return state
      }
      let fieldDesc = this.getPropertiesDescription()[fieldName]
      state.value = false
      state.message = 'Please select a valid value'
      if (Array.isArray(parentObject[fieldName])) {
        if (required && parentObject[fieldName].length === 0) {
          state.value = false
          state.message = 'Please select at least one value'
        } else {
          // if multi select (no value set can be a right value)
          state.value = true
          state.message = true
        }
      } else {
        for (let value in fieldDesc.values) {
          if (fieldDesc.values[value].value === parentObject[fieldName]) {
            state.value = true
            state.message = true
          }
        }
      }
      if (fieldName === 'perturbations' && parentObject.spacecraft != null) {
        if (
          parentObject[fieldName] &&
          parentObject[fieldName].includes('SRP') &&
          !parentObject.spacecraft.srpModelDefined
        ) {
          state.value = false
          state.message =
            'Sun radiation pressure model not defined in spacecraft'
        }
        if (
          parentObject[fieldName] &&
          parentObject[fieldName].includes('DRAG') &&
          !parentObject.spacecraft.dragModelDefined
        ) {
          state.value = false
          state.message = 'Drag model not defined in spacecraft'
        }
      }
      return state
    },

    async refreshGroundStations() {
      let self = this
      await this.$http
        .get(this.getAPI(`groundstationdefinitions`))
        .then((response) => {
          self.groundStationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of ground stations. ',
            e,
            'notification',
            'error'
          )
        })
    }
  }
}
</script>
